// Simple color-enabled debug implementation for browser

// Simple hook system for capturing logs
export interface DebugLogEntry {
  namespace: string;
  level: string;
  message: string; // Plain text message
  formattedMessage?: string; // HTML formatted message with colors and styling
  args: any[];
  timestamp: number;
  template?: string; // The original format string (e.g., "Setting rotation to %s°")
}

export type DebugHook = (entry: DebugLogEntry) => void;

// Hook storage
const rawHooks = new Set<DebugHook>(); // Unfiltered logs (every debug call)
const filteredHooks = new Set<DebugHook>(); // Filtered logs (only console output)

export const debugHooks = {
  // Add hook for raw/unfiltered logs (every debug call, even disabled namespaces)
  addRaw: (hook: DebugHook) => {
    rawHooks.add(hook);
    return () => {
      rawHooks.delete(hook);
    }; // Return cleanup function
  },

  // Add hook for filtered/processed logs (only what appears in console)
  addFiltered: (hook: DebugHook) => {
    filteredHooks.add(hook);
    return () => {
      filteredHooks.delete(hook);
    }; // Return cleanup function
  },

  // Legacy method - defaults to filtered logs (most common use case)
  add: (hook: DebugHook) => {
    return debugHooks.addFiltered(hook);
  },

  // Remove a hook from both types
  remove: (hook: DebugHook) => {
    const removedFromRaw = rawHooks.delete(hook);
    const removedFromFiltered = filteredHooks.delete(hook);
    return removedFromRaw || removedFromFiltered;
  },

  // Clear all hooks
  clear: () => {
    rawHooks.clear();
    filteredHooks.clear();
  },
};

const colors = [
  "#0000CC",
  "#0000FF",
  "#0033CC",
  "#0033FF",
  "#0066CC",
  "#0066FF",
  "#0099CC",
  "#0099FF",
  "#00CC00",
  "#00CC33",
  "#00CC66",
  "#00CC99",
  "#00CCCC",
  "#00CCFF",
  "#3300CC",
  "#3300FF",
  "#3333CC",
  "#3333FF",
  "#3366CC",
  "#3366FF",
  "#3399CC",
  "#3399FF",
  "#33CC00",
  "#33CC33",
  "#33CC66",
  "#33CC99",
  "#33CCCC",
  "#33CCFF",
  "#6600CC",
  "#6600FF",
  "#6633CC",
  "#6633FF",
  "#66CC00",
  "#66CC33",
  "#9900CC",
  "#9900FF",
  "#9933CC",
  "#9933FF",
  "#99CC00",
  "#99CC33",
  "#CC0000",
  "#CC0033",
  "#CC0066",
  "#CC0099",
  "#CC00CC",
  "#CC00FF",
  "#CC3300",
  "#CC3333",
  "#CC3366",
  "#CC3399",
  "#CC33CC",
  "#CC33FF",
  "#CC6600",
  "#CC6633",
  "#CC9900",
  "#CC9933",
  "#CCCC00",
  "#CCCC33",
  "#FF0000",
  "#FF0033",
  "#FF0066",
  "#FF0099",
  "#FF00CC",
  "#FF00FF",
  "#FF3300",
  "#FF3333",
  "#FF3366",
  "#FF3399",
  "#FF33CC",
  "#FF33FF",
  "#FF6600",
  "#FF6633",
  "#FF6666",
  "#FF6699",
  "#FF66CC",
  "#FF66FF",
  "#FF9900",
  "#FF9933",
  "#FF9966",
  "#FF9999",
  "#FF99CC",
  "#FF99FF",
  "#FFCC00",
  "#FFCC33",
  "#FFCC66",
  "#FFCC99",
  "#FFCCCC",
  "#FFCCFF",
];

// Hash function to get consistent color for namespace
const selectColor = (namespace: string): string => {
  let hash = 0;
  for (let i = 0; i < namespace.length; i++) {
    hash = (hash << 5) - hash + namespace.charCodeAt(i);
    hash = hash & hash;
  }
  return colors[Math.abs(hash) % colors.length];
};

// Store namespace colors
const namespaceColors = new Map<string, string>();

// Track longest namespace for alignment
let longestNamespace = 0;

// Auto-throttle tracking
interface ThrottleInfo {
  timestamps: number[];
  suppressedCount: number;
  lastShownTime: number;
  trailingTimeout?: NodeJS.Timeout | number;
  lastMessage?: {
    format: string;
    args: any[];
    capturedStack?: string; // ACTUALLY STORE THE FUCKING STACK
  };
}
const autoThrottleMap = new Map<string, ThrottleInfo>();
const uniqueThrottleMap = new Map<string, ThrottleInfo>(); // namespace:template -> ThrottleInfo

// Stack trace auto-expand tracking
const stackAutoExpandMap = new Map<string, number>(); // namespace -> last expanded timestamp

// VLQ decoding for source maps
const VLQ_BASE_SHIFT = 5;
const VLQ_BASE = 1 << VLQ_BASE_SHIFT; // 32
const VLQ_BASE_MASK = VLQ_BASE - 1; // 31
const VLQ_CONTINUATION_BIT = VLQ_BASE; // 32

const BASE64_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const BASE64_MAP: { [key: string]: number } = {};
for (let i = 0; i < BASE64_CHARS.length; i++) {
  BASE64_MAP[BASE64_CHARS[i]] = i;
}

function decodeVLQ(encoded: string): number[] {
  const decoded: number[] = [];
  let index = 0;

  while (index < encoded.length) {
    let value = 0;
    let shift = 0;
    let continuation = true;

    while (continuation && index < encoded.length) {
      const digit = BASE64_MAP[encoded[index++]];
      if (digit === undefined) {
        throw new Error("Invalid base64 digit: " + encoded[index - 1]);
      }

      continuation = (digit & VLQ_CONTINUATION_BIT) !== 0;
      value += (digit & VLQ_BASE_MASK) << shift;
      shift += VLQ_BASE_SHIFT;
    }

    // Convert from VLQ sign encoding
    const shouldNegate = (value & 1) === 1;
    value = value >> 1;
    if (shouldNegate) {
      value = -value;
    }

    decoded.push(value);
  }

  return decoded;
}

function parseMappings(mappings: string, sources: string[]): Map<string, any> {
  const lines = mappings.split(";");
  const decodedMappings = new Map<string, any>();

  let generatedLine = 0;
  let previousGeneratedColumn = 0;
  let previousOriginalLine = 0;
  let previousOriginalColumn = 0;
  let previousSource = 0;

  for (const line of lines) {
    generatedLine++;
    previousGeneratedColumn = 0;

    const segments = line.split(",");
    for (const segment of segments) {
      if (!segment) continue;

      const decoded = decodeVLQ(segment);
      if (decoded.length === 0) continue;

      // Decode the segment
      const generatedColumn = previousGeneratedColumn + decoded[0];
      previousGeneratedColumn = generatedColumn;

      if (decoded.length > 1) {
        const sourceIndex = previousSource + decoded[1];
        previousSource = sourceIndex;

        const originalLine = previousOriginalLine + decoded[2];
        previousOriginalLine = originalLine;

        const originalColumn = previousOriginalColumn + decoded[3];
        previousOriginalColumn = originalColumn;

        // Store mapping for quick lookup
        const key = `${generatedLine}:${generatedColumn}`;
        decodedMappings.set(key, {
          source: sources[sourceIndex],
          originalLine: originalLine + 1, // VLQ uses 0-based, we want 1-based
          originalColumn,
        });
      }
    }
  }

  return decodedMappings;
}

// Cache for source maps
const sourceMapsCache = new Map<string, any>();

// Timestamp tracking for debug library compatibility
const lastMessageTime = new Map<string, number>(); // namespace -> last message timestamp
const appStartTime = Date.now(); // Track when the app started for relative time

// Configuration per namespace pattern
interface DebugConfig {
  pattern?: string;
  autoThrottle?: boolean; // Enable auto-throttling (legacy, same as throttle: 'auto')
  throttle?: boolean | "auto" | number | string; // Throttle: false, 'auto', cooldown ms, or 'auto-1000'
  throttleWindow?: number; // Window for auto-throttle detection (ms)
  throttleThreshold?: number; // Threshold for auto-throttle (msgs in window)
  throttleCooldown?: number; // Cooldown when throttling (ms between messages)
  collapse?: boolean | number; // Collapse objects: false (expand), true (collapse at 60 chars), or number for custom limit
  align?: boolean; // Align messages by padding namespace
  precision?: number; // Max decimal places for ALL numbers (default: unlimited)
  stack?: boolean | "collapsed" | "auto" | number | string; // Show stack trace: true, 'collapsed', 'auto', number (depth), or 'auto 5', 'collapsed 3'
  stackDepth?: number; // How many stack frames to show (default: 3)
  stackAll?: boolean; // Show all frames except debugx internals (includes node_modules, webpack, etc)
  stackLink?: boolean; // Show VSCode links in stack traces
  stackMap?: boolean | "auto" | "inline" | "external" | "eval"; // Parse source maps for accurate line numbers
  line?: number; // Manually specify line number for debugging
  timestamp?: boolean; // Show elapsed time (legacy, same as perf: true)
  time?: boolean | "short" | "medium" | "long" | "iso" | "unix" | "relative"; // Show actual time at beginning of log
  perf?: boolean; // Show performance/elapsed time at end of log
  color?: string; // Custom color for namespace (color name or hex: 'red', '#f00', '#ff0000')
  foreground?: string; // Custom color for message text (color name or hex: 'blue', '#00f', '#0000ff')
  levelPostfix?: boolean; // Show level postfix in namespace (e.g., namespace:error)
  objectDepth?: number; // Max depth for object serialization (default: 3)
  $replace?: boolean; // When used inline, replace config instead of merging
}

// Named configs storage
const namedConfigs = new Map<string, DebugConfig>();

// Default values
const DEFAULT_THROTTLE_WINDOW = 5000; // 5 seconds
const DEFAULT_THROTTLE_THRESHOLD = 10; // More than 10 messages in 5 seconds triggers throttling
const DEFAULT_THROTTLE_COOLDOWN = 1000; // Show one message per ~second when throttled
const DEFAULT_COLLAPSE_LIMIT = 60; // Max chars for collapsed objects
const DEFAULT_OBJECT_DEPTH = 3; // Max depth for object serialization

// Built-in named configs
const BUILTIN_CONFIGS: Record<string, DebugConfig> = {
  // Throttle configs - for auto-throttling spam
  "throttle.default": {
    autoThrottle: true,
    throttleWindow: DEFAULT_THROTTLE_WINDOW,
    throttleThreshold: DEFAULT_THROTTLE_THRESHOLD,
    throttleCooldown: DEFAULT_THROTTLE_COOLDOWN,
  },
  "throttle.strict": {
    throttle: "auto-2000", // Auto-throttle with 2 second cooldown
    throttleWindow: 3000, // 3 second window
    throttleThreshold: 5, // Only 5 messages before throttling
  },
  "throttle.aggressive": {
    autoThrottle: true,
    throttleWindow: 2000,
    throttleThreshold: 3,
    throttleCooldown: 3000,
  },
  "throttle.mild": {
    autoThrottle: true,
    throttleWindow: 10000,
    throttleThreshold: 20,
    throttleCooldown: 500,
  },
  "throttle.realtime": {
    autoThrottle: true,
    throttleWindow: 1000,
    throttleThreshold: 10,
    throttleCooldown: 100,
  },
  "throttle.off": {
    autoThrottle: false,
  },
  "throttle.none": {
    autoThrottle: false,
  },
  "throttle.disable": {
    autoThrottle: false,
  },
  // Time format configs
  "time.short": {
    time: "short", // HH:MM:SS
  },
  "time.medium": {
    time: "medium", // HH:MM:SS.mmm
  },
  "time.long": {
    time: "long", // YYYY-MM-DD HH:MM:SS.mmm
  },
  "time.iso": {
    time: "iso", // ISO 8601 format
  },
  "time.unix": {
    time: "unix", // Unix timestamp
  },
  "time.relative": {
    time: "relative", // Show relative time since start
  },
  "time.elapsed": {
    perf: true, // Show elapsed time instead
  },
  // Object expansion configs
  compact: {
    collapse: true,
  },
  noexpand: {
    collapse: true,
  },
  "objects.collapse": {
    collapse: true,
  },
  "objects.expand": {
    collapse: false,
  },
  "objects.default": {
    collapse: false, // Explicitly set to false (expand by default)
  },
  "objects.reset": {
    collapse: false, // Reset to expanding
  },
  "objects.smart": {
    collapse: true, // Collapse objects when too long
  },
  "objects.auto": {
    collapse: true, // Alias for smart
  },
  // Alignment configs
  align: {
    align: true,
  },
  aligned: {
    align: true,
  },
  "format.align": {
    align: true,
  },
  "format.aligned": {
    align: true,
  },
};

// Parse config string (used for both debug and debugConfig keys)
const parseConfigString = (configStr: string): Map<string, DebugConfig> => {
  if (!configStr) return new Map();

  // Check for $replace directive
  const shouldReplace = configStr.startsWith("$replace:");
  const actualConfigStr = shouldReplace ? configStr.substring(9) : configStr;

  const patternConfigs = new Map<string, DebugConfig>();

  // Only clear named configs if replacing
  if (shouldReplace) {
    namedConfigs.clear();
  }

  // First pass: parse named configs (start with #)
  // Second pass: parse patterns that reference configs

  // Split by commas or newlines but not inside braces
  const parts: string[] = [];
  let current = "";
  let braceDepth = 0;

  for (let i = 0; i < actualConfigStr.length; i++) {
    const char = actualConfigStr[i];
    if (char === "{") braceDepth++;
    else if (char === "}") braceDepth--;
    else if ((char === "," || char === "\n") && braceDepth === 0) {
      // Remove comments before adding to parts
      const commentIndex = current.indexOf("# ");
      const cleaned =
        commentIndex !== -1 ? current.substring(0, commentIndex) : current;
      if (cleaned.trim()) parts.push(cleaned.trim());
      current = "";
      continue;
    }
    current += char;
  }
  // Handle last part with comment removal
  const commentIndex = current.indexOf("# ");
  const cleaned =
    commentIndex !== -1 ? current.substring(0, commentIndex) : current;
  if (cleaned.trim()) parts.push(cleaned.trim());

  // Parse each part
  for (const part of parts) {
    // Skip all prefixes (!?+-~) for config parsing - they're handled separately for enable/disable
    let cleanPart = part;
    while (cleanPart && "!?+-~".includes(cleanPart[0])) {
      cleanPart = cleanPart.substring(1);
    }

    const configMatch = cleanPart.match(/^([^{]+)(?:\{([^}]*)\})?$/);
    if (!configMatch) continue;

    const nameOrPattern = configMatch[1].trim();
    const options = configMatch[2];

    // Parse options into config object
    const config: DebugConfig = {};
    if (options) {
      // Support both comma and semicolon as separators
      const opts = options.split(/[,;]/);
      for (const opt of opts) {
        const [key, value] = opt.split(":").map((s) => s.trim());

        // Handle null values - set to a special marker to indicate "use default"
        if (value === "null" || value === "undefined") {
          (config as any)[key] = null; // Use null as a marker for "reset to default"
          continue;
        }

        // Try to parse as number first
        const numValue = Number(value);
        if (!isNaN(numValue) && value !== "") {
          (config as any)[key] = numValue;
        }
        // Check for boolean
        else if (value === "true" || value === "false") {
          (config as any)[key] = value === "true";
        }
        // Otherwise treat as string
        else {
          (config as any)[key] = value;
        }
      }
    } else {
      // No options means enable auto-throttle with defaults
      config.autoThrottle = true;
    }

    // Check if it's a named config definition
    if (nameOrPattern.startsWith("#")) {
      const configName = nameOrPattern.substring(1);
      namedConfigs.set(configName, config);
    } else {
      // It's a pattern, possibly with config reference
      const [pattern, ...configRefs] = nameOrPattern.split("#");

      // Start with empty config
      let finalConfig: DebugConfig = {};

      // Apply referenced configs in order
      for (const ref of configRefs) {
        // Check built-in configs first, then user-defined
        const referencedConfig = BUILTIN_CONFIGS[ref] || namedConfigs.get(ref);
        if (referencedConfig) {
          // Merge configs (referenced configs first)
          finalConfig = { ...finalConfig, ...referencedConfig };
        }
      }

      // Apply inline config last (overrides referenced configs)
      // Include null values in the config so we know to remove them during merge
      for (const [key, value] of Object.entries(config)) {
        // Store all values including null (null means "reset to default")
        (finalConfig as any)[key] = value;
      }

      // Store the final config for this pattern
      patternConfigs.set(pattern.trim(), finalConfig);
    }
  }

  return patternConfigs;
};

// Store worker debugConfig if passed
let workerDebugConfigStr: string = "";

export const setWorkerDebugConfigStr = (config: string) => {
  workerDebugConfigStr = config;
};

// Parse debugConfig from localStorage or worker config
const parseDebugConfig = (): Map<string, DebugConfig> => {
  const configStr = isWorker
    ? workerDebugConfigStr
    : typeof localStorage !== "undefined"
      ? localStorage.getItem("debugConfig")
      : null;
  return parseConfigString(configStr || "");
};

// Get config for a specific namespace (with parent inheritance)
// Checks both debug patterns (with inline configs) and debugConfig
const getNamespaceConfig = (
  namespace: string,
  includeParents: boolean = false,
): DebugConfig | null => {
  // Parse configs from both sources
  const debugConfigConfigs = parseDebugConfig();
  const userDebugConfig = isWorker
    ? workerDebugConfig
    : typeof localStorage !== "undefined"
      ? localStorage.getItem("debug") || ""
      : "";
  const fullDebugConfig =
    defaultDebugConfig + (userDebugConfig ? "\n" + userDebugConfig : "");
  const debugPatternConfigs = parseConfigString(fullDebugConfig);

  let mergedConfig: DebugConfig = {};

  if (includeParents) {
    // Check parent namespaces (e.g., for "a:b:c", check "a", then "a:b", then "a:b:c")
    const parts = namespace.split(":");
    for (let i = 1; i <= parts.length; i++) {
      const parentNamespace = parts.slice(0, i).join(":");

      // Check debug patterns first (higher priority)
      if (debugPatternConfigs.has(parentNamespace)) {
        mergedConfig = {
          ...mergedConfig,
          ...debugPatternConfigs.get(parentNamespace)!,
        };
      }
      // Then check debugConfig
      if (debugConfigConfigs.has(parentNamespace)) {
        mergedConfig = {
          ...mergedConfig,
          ...debugConfigConfigs.get(parentNamespace)!,
        };
      }
    }
  }

  // Check wildcard patterns from both sources FIRST
  const allConfigs = new Map([...debugConfigConfigs, ...debugPatternConfigs]);
  for (const [pattern, config] of allConfigs.entries()) {
    if (pattern.includes("*")) {
      const regexPattern = pattern
        .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
        .replace(/\*/g, ".*");
      const regex = new RegExp("^" + regexPattern + "$");
      if (regex.test(namespace)) {
        mergedConfig = { ...mergedConfig, ...config };
      }
    }
  }

  // Then apply exact matches on top (if not doing parent inheritance)
  if (!includeParents) {
    // Check exact match in debug patterns
    if (debugPatternConfigs.has(namespace)) {
      mergedConfig = {
        ...mergedConfig,
        ...debugPatternConfigs.get(namespace)!,
      };
    }
    // Then check debugConfig
    if (debugConfigConfigs.has(namespace)) {
      mergedConfig = { ...mergedConfig, ...debugConfigConfigs.get(namespace)! };
    }
  }

  return Object.keys(mergedConfig).length > 0 ? mergedConfig : null;
};

// Simple message formatting with depth control
const formatMessage = (
  format: string,
  args: any[],
  objectDepth: number = 3,
): string => {
  let message = format;
  let argIndex = 0;

  // Process format specifiers first (including %.Nf for float precision)
  message = message.replace(/%(?:\.(\d+))?[sdifjoO%]/g, (match, precision) => {
    if (match === "%%") return "%";
    if (argIndex < args.length) {
      const arg = args[argIndex++];

      // Handle float/decimal with precision (e.g., %.4f, %.2d)
      if (precision && (match.includes("f") || match.includes("d"))) {
        if (typeof arg === "number") {
          return arg.toFixed(parseInt(precision));
        }
        // If not a number but has precision specifier, just convert to string
        return String(arg);
      }

      // Handle regular %f without precision - just convert to string
      if (match.includes("f") && typeof arg === "number") {
        return String(arg);
      }

      if (typeof arg === "object" && arg !== null) {
        return stringifyWithDepth(arg, objectDepth);
      }
      return String(arg);
    }
    return match;
  });

  // Append any remaining args that weren't consumed by format specifiers
  const remainingArgs = args.slice(argIndex);
  if (remainingArgs.length > 0) {
    const extraArgs = remainingArgs.map((arg) => {
      if (typeof arg === "object" && arg !== null) {
        return stringifyWithDepth(arg, objectDepth);
      }
      return String(arg);
    });
    message += (message ? " " : "") + extraArgs.join(" ");
  }

  return message;
};

// Convert console format string to HTML with colors and config options
const formatMessageToHtml = (
  message: string, // Pre-formatted message (including suppress count)
  _args: any[],
  namespace: string,
  namespaceColor: string,
  textColor: string = "#fff",
  _objectDepth: number = 3,
  config: DebugConfig | null = null,
  perfStr: string = "",
  suppressedCount: number = 0,
  timeStr: string = "",
  stackTrace: string = "",
  stackCollapsed: boolean = false,
): string => {
  const parts: string[] = [];

  // Handle time display (clean up %c formatting from console timeStr)
  if (timeStr) {
    // Remove %c prefix that's meant for console styling
    const cleanTimeStr = timeStr.replace(/^%c/, "");
    parts.push(`<span style="color: #888;">${cleanTimeStr}</span>`);
  }

  // Namespace
  parts.push(
    `<span style="color: ${namespaceColor}; font-weight: bold;">${namespace}</span>`,
  );

  // Suppress count with proper gray color
  let processedMessage = message;
  if (suppressedCount > 0 && message.startsWith(`[${suppressedCount}] `)) {
    processedMessage = message.substring(`[${suppressedCount}] `.length);
    parts.push(`<span style="color: #888;">[${suppressedCount}]</span>`);
  }

  // Separate objects from message text for proper perf positioning
  // Look for object patterns that were embedded by formatMessage
  const objectMatches = processedMessage.match(
    /(\{[^}]*\}|\[[^\]]*\]|<span class="debug-object"[^>]*>.*?<\/span>)/g,
  );
  let messageTextOnly = processedMessage;
  let objectsHtml = "";

  if (objectMatches) {
    // Remove objects from message text
    objectMatches.forEach((match) => {
      messageTextOnly = messageTextOnly.replace(match, "").trim();
    });
    // Keep objects for later
    objectsHtml = " " + objectMatches.join(" ");
  }

  // Main message text (without objects)
  parts.push(`<span style="color: ${textColor};">${messageTextOnly}</span>`);

  // Performance timing (after message, before objects)
  if (perfStr) {
    parts.push(`<span style="color: ${namespaceColor};">${perfStr}</span>`);
  }

  // Objects last
  if (objectsHtml) {
    parts.push(objectsHtml);
  }

  let mainDiv = `<div>${parts.join(" ")}</div>`;

  // Stack trace (if present) - make entire line clickable but handle object interactions
  if (stackTrace) {
    // Use the actual stackCollapsed value from debugx main logic
    const stackDisplay = stackCollapsed ? "none" : "block";
    const caretSymbol = stackCollapsed ? "▶" : "▼";

    // Clean up stack trace formatting
    const cleanStackTrace = stackTrace.replace(/^\n/, ""); // Remove leading newline

    // Process stack trace to make VSCode links clickable (only in HTML, not console)
    const processedStackTrace = cleanStackTrace.replace(
      /(    ↳ [^\n]+)\n         (vscode:\/\/file[^\n]+)/g,
      (_, stackLine, vscodeUri) => {
        // Make the filename in the stack line clickable
        return stackLine.replace(
          /at (.+?):(\d+)/,
          `at <a href="${vscodeUri}" style="color: #4a9eff; text-decoration: underline;" title="Open in VSCode">$1:$2</a>`,
        );
      },
    );

    // Create expandable stack trace with clickable main line
    const stackId = `stack-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const caretId = `caret-${stackId}`;

    // Make the entire main line clickable, but add event stopping for debug objects
    const clickHandler = `
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      const content = document.getElementById('${stackId}');
      const caret = document.getElementById('${caretId}');
      if (content.style.display === 'none') {
        content.style.display = 'block';
        caret.textContent = '▼';
      } else {
        content.style.display = 'none';
        caret.textContent = '▶';
      }
    `;

    // Add inline stopPropagation to debug objects to prevent stack trace toggle
    const processedParts = parts.map((part) => {
      if (typeof part === "string" && part.includes('class="debug-object"')) {
        // Add onmousedown with stopPropagation (fires before onclick)
        return part.replace(
          'class="debug-object"',
          'class="debug-object" onmousedown="event.stopPropagation();"',
        );
      }
      return part;
    });

    // Extract first VSCode link for right-aligned filename
    let rightAlignedLink = "";
    const firstVscodeMatch = cleanStackTrace.match(/vscode:\/\/file[^\n]+/);
    if (firstVscodeMatch) {
      const vscodeUri = firstVscodeMatch[0];
      // Extract filename from the first stack line
      const filenameMatch = cleanStackTrace.match(/at (.+?):(\d+)/);
      if (filenameMatch) {
        const filename = filenameMatch[1];
        const lineNum = filenameMatch[2];
        rightAlignedLink = `<a href="${vscodeUri}" style="color: #4a9eff; text-decoration: none; font-size: 9px; opacity: 0.7;" title="Open in VSCode" onmousedown="event.stopPropagation();">${filename}:${lineNum}</a>`;
      }
    }

    // Add caret before the main content - caret and message text clickable, but objects will have stopPropagation
    mainDiv = `<div style="display: flex; align-items: flex-start; cursor: pointer;" onclick="${clickHandler}">
      <span id="${caretId}" style="color: #888; font-size: 10px; margin-right: 4px; flex-shrink: 0; line-height: 1.4; margin-top: 1px;">${caretSymbol}</span>
      <div style="flex: 1;">${processedParts.join(" ")}</div>
      ${rightAlignedLink ? `<div style="margin-left: auto; padding-left: 8px;">${rightAlignedLink}</div>` : ""}
    </div>`;

    // Add the stack trace content below (using div instead of pre to support HTML links)
    mainDiv += `<div id="${stackId}" style="
      display: ${stackDisplay};
      margin: 4px 0 4px 20px;
      padding: 6px;
      background: rgba(255,255,255,0.05);
      border-radius: 3px;
      font-size: 9px;
      border-left: 2px solid ${namespaceColor};
      color: #ccc;
      white-space: pre-wrap;
      font-family: monospace;
      max-height: 300px;
      overflow-y: auto;
      user-select: text;
    ">${processedStackTrace}</div>`;
  }

  return mainDiv;
};

// Create console-style object summary with data-json attribute
const stringifyWithDepth = (obj: any, _maxDepth: number): string => {
  if (typeof obj !== "object" || obj === null) {
    return String(obj);
  }

  try {
    // Get full JSON for data attribute
    const fullJson = JSON.stringify(obj, null, 2);

    // Create console-style summary
    if (Array.isArray(obj)) {
      const preview =
        obj.length <= 5
          ? `[${obj.map((item) => (typeof item === "object" ? "…" : JSON.stringify(item))).join(", ")}]`
          : `(${obj.length}) [${obj
              .slice(0, 5)
              .map((item) =>
                typeof item === "object" ? "…" : JSON.stringify(item),
              )
              .join(", ")}, …]`;

      return `<span class="debug-object" data-json="${fullJson.replace(/"/g, "&quot;")}" style="color: #4a9eff; cursor: pointer;" title="Click to expand">${preview}</span>`;
    }

    // Object summary
    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return `<span class="debug-object" data-json="${fullJson.replace(/"/g, "&quot;")}" style="color: #4a9eff; cursor: pointer;" title="Click to expand">{}</span>`;
    }

    const showKeys = keys.slice(0, 5);
    const preview = showKeys.map((key) => {
      const value = obj[key];
      if (typeof value === "string") {
        return `${key}: "${value.length > 20 ? value.slice(0, 20) + "…" : value}"`;
      } else if (typeof value === "object" && value !== null) {
        return `${key}: {…}`;
      } else {
        return `${key}: ${JSON.stringify(value)}`;
      }
    });

    const summary =
      keys.length > 5
        ? `{${preview.join(", ")}, …${keys.length - 5} more}`
        : `{${preview.join(", ")}}`;

    return `<span class="debug-object" data-json="${fullJson.replace(/"/g, "&quot;")}" style="color: #4a9eff; cursor: pointer;" title="Click to expand">${summary}</span>`;
  } catch (error) {
    return "[Object (stringify failed)]";
  }
};

// Call raw/unfiltered hooks
const callRawHooks = (entry: DebugLogEntry) => {
  for (const hook of rawHooks) {
    try {
      hook(entry);
    } catch (error) {
      console.error("Debug raw hook threw an error:", error);
    }
  }
};

// Call filtered/processed hooks
const callFilteredHooks = (entry: DebugLogEntry) => {
  for (const hook of filteredHooks) {
    try {
      hook(entry);
    } catch (error) {
      console.error("Debug filtered hook threw an error:", error);
    }
  }
};

// Internal default debug config (set by setDefaultDebugConfig)
let defaultDebugConfig = "";

// Utility to set default debug configuration (call from App.tsx on startup)
export const setDefaultDebugConfig = (config: string) => {
  defaultDebugConfig = config;
};

// Check if we're in a worker context
const isWorker =
  typeof self !== "undefined" &&
  typeof (self as any).WorkerGlobalScope !== "undefined" &&
  typeof (self as any).importScripts === "function";

// Store worker debug config if passed via postMessage
let workerDebugConfig: string = "";

// Export function to set worker debug config
export const setWorkerDebugConfig = (config: string) => {
  workerDebugConfig = config;
};

// Simple debug implementation
const DebugLib = (namespace: string, defaultConfig?: Partial<DebugConfig>) => {
  // Get or assign color for this namespace
  if (!namespaceColors.has(namespace)) {
    namespaceColors.set(namespace, selectColor(namespace));
  }
  const color = namespaceColors.get(namespace)!;

  // Update longest namespace tracking
  if (namespace.length > longestNamespace) {
    longestNamespace = namespace.length;
  }

  return (format: string | any, ...args: any[]) => {
    // In worker context, use passed config; otherwise use localStorage
    const userDebugSetting = isWorker
      ? workerDebugConfig
      : typeof localStorage !== "undefined"
        ? localStorage.getItem("debug") || ""
        : "";
    const debugSetting =
      defaultDebugConfig + (userDebugSetting ? "\n" + userDebugSetting : "");
    if (!debugSetting) return;

    // Helper to check pattern matching
    const matchesPattern = (
      ns: string,
      pattern: string,
      isDisable: boolean = false,
    ): boolean => {
      if (!pattern) return false;
      if (pattern === "*") return true;
      if (pattern === ns) return true;

      // For disable patterns, also match sub-namespaces
      // e.g., "-bikedash:visibility" matches "bikedash:visibility:error"
      if (isDisable && ns.startsWith(pattern + ":")) {
        return true;
      }

      if (pattern.includes("*")) {
        const regexPattern = pattern
          .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
          .replace(/\*/g, ".*");
        const regex = new RegExp("^" + regexPattern + "$");
        return regex.test(ns);
      }

      return false;
    };

    // Parse debug key to get both enable/disable patterns and configs
    const debugPatterns = parseConfigString(debugSetting);

    // Collect configs for this specific namespace
    let baseConfig: DebugConfig = {};
    let forceOverrideConfig: DebugConfig | null = null; // For ! prefix patterns
    let softConfig: DebugConfig | null = null; // For ? prefix patterns (don't override defaults)

    // Parse the raw debug string to check for enable/disable prefixes
    // Need to handle braces properly - don't split on commas inside braces
    const rawParts: string[] = [];
    let current = "";
    let braceDepth = 0;
    for (let i = 0; i < debugSetting.length; i++) {
      const char = debugSetting[i];
      if (char === "{") braceDepth++;
      else if (char === "}") braceDepth--;
      else if ((char === "," || char === "\n") && braceDepth === 0) {
        if (current.trim()) rawParts.push(current.trim());
        current = "";
        continue;
      }
      current += char;
    }
    if (current.trim()) rawParts.push(current.trim());

    // Remove comments
    const parts = rawParts
      .map((s) => {
        const commentIndex = s.indexOf("# ");
        return commentIndex !== -1
          ? s.substring(0, commentIndex).trim()
          : s.trim();
      })
      .filter((s) => s);

    // Process patterns in order - accumulate all matching configs
    let finalEnabled = false;
    let finalDisabled = false;
    let accumulatedConfig: DebugConfig = {};

    for (const part of parts) {
      if (!part) continue;

      // Parse all prefixes (can be combined like !~, ?+, etc.)
      let cleanPart = part;
      let hasForceOverride = false;
      let hasSoftConfig = false;
      let isConfigOnly = false;
      let isDisable = false;

      // Check for prefixes in any order/combination
      while (cleanPart && "!?+-~".includes(cleanPart[0])) {
        const prefix = cleanPart[0];
        if (prefix === "!") hasForceOverride = true;
        if (prefix === "?") hasSoftConfig = true;
        if (prefix === "~") isConfigOnly = true;
        if (prefix === "-") isDisable = true;
        if (prefix === "+") {
        } // Explicit enable, no special handling needed
        cleanPart = cleanPart.substring(1);
      }

      // Skip config-only patterns
      if (isConfigOnly) continue;

      // Extract pattern
      const pattern = cleanPart.split(/[#{]/)[0].trim();

      // Check if this pattern matches our namespace
      const matches = isDisable
        ? matchesPattern(namespace, pattern, true) // For disable, also match children
        : matchesPattern(namespace, pattern, false); // For enable, normal matching

      if (matches) {
        // Update final state - last match wins
        if (isDisable) {
          finalEnabled = false;
          finalDisabled = true;
        } else {
          finalEnabled = true;
          finalDisabled = false;

          // Get the pattern config and ACCUMULATE it
          if (debugPatterns.has(pattern)) {
            const patternConfig = debugPatterns.get(pattern)!;

            // Merge configs in order - later patterns override earlier ones
            // but ONLY for the keys they specify
            for (const [key, value] of Object.entries(patternConfig)) {
              if (value === null) {
                // null means reset to default - remove the key
                delete (accumulatedConfig as any)[key];
              } else {
                (accumulatedConfig as any)[key] = value;
              }
            }

            // Handle special prefix configs separately
            if (hasForceOverride) {
              forceOverrideConfig = forceOverrideConfig || {};
              Object.assign(forceOverrideConfig, patternConfig);
            } else if (hasSoftConfig) {
              softConfig = softConfig || {};
              Object.assign(softConfig, patternConfig);
            }
          }
        }
      }
    }

    // Use the accumulated config as our base config
    if (finalEnabled) {
      baseConfig = accumulatedConfig;
    }

    // Always call unfiltered hooks before checking if enabled
    const currentTime = Date.now();

    // Extract log level using same logic as later in the function
    const earlyNamespaceParts = namespace.split(":");
    const earlyPossibleLevel =
      earlyNamespaceParts[earlyNamespaceParts.length - 1].toLowerCase();
    const currentLogLevel = [
      "error",
      "warn",
      "warning",
      "info",
      "debug",
      "trace",
    ].includes(earlyPossibleLevel)
      ? earlyPossibleLevel
      : "log";

    // Create basic log entry for hooks (regardless of filtering)
    const basicEntry: DebugLogEntry = {
      namespace,
      level: currentLogLevel,
      message: formatMessage(
        typeof format === "string" ? format : String(format),
        args,
        DEFAULT_OBJECT_DEPTH,
      ),
      args: args,
      timestamp: currentTime,
    };

    // Call raw hooks for all logs (even disabled ones)
    callRawHooks(basicEntry);

    // Check final state
    if (!finalEnabled || finalDisabled) return;

    // Check if first argument is a config object
    let inlineConfig: DebugConfig | null = null;
    let actualFormat: string;
    let actualArgs: any[];
    let capturedStack: string | undefined;

    // Check for captured stack (from throttling) FIRST
    if (
      format &&
      typeof format === "object" &&
      (format as any).__capturedStack
    ) {
      capturedStack = (format as any).__capturedStack;
      actualFormat = args[0] as string;
      actualArgs = args.slice(1);
    } else if (
      format &&
      typeof format === "object" &&
      !Array.isArray(format) &&
      format.constructor === Object &&
      (format.$replace !== undefined ||
        format.autoThrottle !== undefined ||
        format.throttle !== undefined ||
        format.collapse !== undefined ||
        format.align !== undefined ||
        format.precision !== undefined ||
        format.throttleWindow !== undefined ||
        format.throttleThreshold !== undefined ||
        format.throttleCooldown !== undefined ||
        format.stack !== undefined ||
        format.stackDepth !== undefined ||
        format.timestamp !== undefined ||
        format.time !== undefined ||
        format.perf !== undefined ||
        format.color !== undefined ||
        format.foreground !== undefined ||
        format.levelPostfix !== undefined ||
        format.stackLink !== undefined ||
        format.line !== undefined ||
        format.l !== undefined ||
        format.L !== undefined ||
        format.stackMap !== undefined)
    ) {
      // First arg is a config object
      inlineConfig = format;
      actualFormat = args[0] as string;
      actualArgs = args.slice(1);
    } else {
      // Normal debug call
      actualFormat = format as string;
      actualArgs = args;
    }

    const now = Date.now();

    // Build config hierarchy:
    // 1. Soft configs (? prefix) - only apply if no code default exists for that key
    // 2. Wildcard configs from localStorage (e.g., * pattern) - base settings
    // 3. defaultConfig (namespace-specific defaults from code) - overrides wildcards
    // 4. Exact match configs from localStorage - overrides both wildcards and defaults
    // 5. Force override configs (! prefix) - overrides EVERYTHING
    // 6. Inline config (highest priority)

    // Helper to merge configs, handling null values
    const mergeConfig = (target: DebugConfig, source: DebugConfig) => {
      for (const [key, value] of Object.entries(source)) {
        if (value === null) {
          // null means reset to default - remove the key
          delete (target as any)[key];
        } else {
          (target as any)[key] = value;
        }
      }
      return target;
    };

    // Apply soft configs if we have them
    if (softConfig) {
      mergeConfig(baseConfig, softConfig);
    }

    // Apply defaultConfig to add namespace-specific defaults
    if (defaultConfig) {
      baseConfig = { ...baseConfig, ...defaultConfig };
    }

    // Fall back to debugConfig if we found no patterns in debug key
    if (!Object.keys(baseConfig).length && !softConfig) {
      const debugConfigMatch = getNamespaceConfig(namespace);
      if (debugConfigMatch) {
        baseConfig = { ...baseConfig, ...debugConfigMatch };
      }
    }

    // Apply force override configs (! prefix) - these override EVERYTHING including code defaults
    if (forceOverrideConfig) {
      mergeConfig(baseConfig, forceOverrideConfig);
    }

    let config = baseConfig;

    // Apply inline config if provided (highest priority)
    if (inlineConfig) {
      if (inlineConfig.$replace) {
        // Replace mode - use only inline config (still keeps defaultConfig as base)
        config = { ...defaultConfig, ...inlineConfig };
        delete config.$replace; // Remove the directive
      } else {
        // Merge mode - inline config overrides everything
        config = { ...config, ...inlineConfig };
      }
    }

    // Parse throttle configuration
    let autoThrottleEnabled = config?.autoThrottle ?? false;
    let alwaysThrottle = false;
    let uniqueThrottle = false;
    let throttleCooldown =
      config?.throttleCooldown ?? DEFAULT_THROTTLE_COOLDOWN;

    // Handle various throttle config formats
    if (config?.throttle !== undefined) {
      const throttleValue = config.throttle;
      if (throttleValue === "auto" || throttleValue === true) {
        autoThrottleEnabled = true;
      } else if (throttleValue === false) {
        autoThrottleEnabled = false;
        alwaysThrottle = false;
      } else if (typeof throttleValue === "number") {
        // Number means always throttle with this cooldown
        alwaysThrottle = true;
        throttleCooldown = throttleValue;
      } else if (
        typeof throttleValue === "string" &&
        throttleValue.startsWith("auto")
      ) {
        // Format: 'auto-cooldown-window-threshold' or 'auto cooldown window threshold'
        // 'auto-1000' or 'auto 1000' = cooldown 1000ms, window defaults to 1000ms
        // 'auto-2000-10' or 'auto 2000 10' = cooldown 2000ms, window defaults to 2000ms, threshold 10
        // 'auto-500-2000-10' or 'auto 500 2000 10' = cooldown 500ms, window 2000ms, threshold 10
        // 'auto unique 1000' = unique throttling with 1000ms cooldown

        // Check for unique throttling option
        if (throttleValue.includes("unique")) {
          uniqueThrottle = true;
        } else {
          autoThrottleEnabled = true;
        }

        // Helper to parse time values with units
        const parseTime = (value: string): number | null => {
          if (!value) return null;

          // Check for units
          const match = value.match(/^(\d+(?:\.\d+)?)(ms|s|m)?$/i);
          if (!match) {
            // Try parsing as plain number
            const num = parseInt(value);
            return isNaN(num) ? null : num;
          }

          const [, numStr, unit = "ms"] = match;
          const num = parseFloat(numStr);
          if (isNaN(num)) return null;

          // Convert to milliseconds
          switch (unit.toLowerCase()) {
            case "s":
              return num * 1000;
            case "m":
              return num * 60000;
            case "ms":
            default:
              return num;
          }
        };

        // Handle both space and hyphen separators
        let parts: string[];
        if (throttleValue.includes(" ")) {
          // Space-separated: 'auto 1000 2000 10' or 'auto unique 1000'
          parts = throttleValue
            .split(" ")
            .slice(1)
            .filter((p) => p && p !== "unique"); // Remove 'auto', 'unique' and empty strings
        } else {
          // Hyphen-separated: 'auto-1000-2000-10' or 'auto-1s-2s-10'
          parts = throttleValue
            .substring(5)
            .split("-")
            .filter((p) => p !== "unique");
        }

        if (parts.length >= 1) {
          const cooldown = parseTime(parts[0]);
          if (cooldown !== null) {
            throttleCooldown = cooldown;
            // Window defaults to cooldown if not specified
            config.throttleWindow = cooldown;
          }
        }

        if (parts.length >= 2) {
          // Check if second param is a time value or threshold
          const timeValue = parseTime(parts[1]);
          const intValue = parseInt(parts[1]);

          if (parts.length === 2) {
            // Two params: could be cooldown-threshold or cooldown-window
            if (
              intValue < 100 &&
              !parts[1].includes("ms") &&
              !parts[1].includes("s")
            ) {
              // Small number without units is likely threshold
              config.throttleThreshold = intValue;
            } else if (timeValue !== null) {
              // Otherwise treat as window time
              config.throttleWindow = timeValue;
            }
          } else {
            // Three params: cooldown-window-threshold
            if (timeValue !== null) {
              config.throttleWindow = timeValue;
            }
          }
        }

        if (parts.length >= 3) {
          const threshold = parseInt(parts[2]);
          if (!isNaN(threshold)) {
            config.throttleThreshold = threshold;
          }
        }
      } else if (typeof throttleValue === "string") {
        // Handle simple time strings like "2s", "1000ms", "500", etc.
        const parseTime = (value: string): number | null => {
          if (!value) return null;

          // Check for units
          const match = value.match(/^(\d+(?:\.\d+)?)(ms|s|m)?$/i);
          if (!match) {
            // Try parsing as plain number
            const num = parseInt(value);
            return isNaN(num) ? null : num;
          }

          const [, numStr, unit = "ms"] = match;
          const num = parseFloat(numStr);
          if (isNaN(num)) return null;

          // Convert to milliseconds
          switch (unit.toLowerCase()) {
            case "s":
              return num * 1000;
            case "m":
              return num * 60000;
            case "ms":
            default:
              return num;
          }
        };

        const parsedTime = parseTime(throttleValue);
        if (parsedTime !== null) {
          // Simple time string means always throttle with this cooldown
          alwaysThrottle = true;
          throttleCooldown = parsedTime;
        } else if (throttleValue === "unique") {
          // Standalone unique throttling with default cooldown
          uniqueThrottle = true;
        } else if (throttleValue.startsWith("unique ")) {
          // Format: "unique 1000" = unique throttling with 1000ms cooldown
          uniqueThrottle = true;
          const parts = throttleValue.split(" ").slice(1); // Remove 'unique'
          if (parts.length >= 1) {
            const parseTime = (value: string): number | null => {
              if (!value) return null;
              const match = value.match(/^(\d+(?:\.\d+)?)(ms|s|m)?$/i);
              if (!match) {
                const num = parseInt(value);
                return isNaN(num) ? null : num;
              }
              const [, numStr, unit = "ms"] = match;
              const num = parseFloat(numStr);
              if (isNaN(num)) return null;
              switch (unit.toLowerCase()) {
                case "s":
                  return num * 1000;
                case "m":
                  return num * 60000;
                case "ms":
                default:
                  return num;
              }
            };
            const cooldown = parseTime(parts[0]);
            if (cooldown !== null) {
              throttleCooldown = cooldown;
            }
          }
        }
      }
    }

    const alignEnabled = config?.align ?? false;
    const timestampEnabled = config?.timestamp ?? false;
    const timeEnabled = config?.time ?? false; // Show actual time at beginning
    const perfEnabled = config?.perf ?? timestampEnabled; // Show elapsed time (perf or timestamp)

    // Detect log level from namespace
    const namespaceParts = namespace.split(":");
    const possibleLevel =
      namespaceParts[namespaceParts.length - 1].toLowerCase();
    const logLevel = [
      "error",
      "warn",
      "warning",
      "info",
      "debug",
      "trace",
    ].includes(possibleLevel)
      ? possibleLevel
      : "log";

    // Map level to console method
    const consoleMethod =
      {
        error: console.error,
        warn: console.warn,
        warning: console.warn,
        info: console.info,
        debug: console.debug,
        trace: console.trace,
        log: console.log,
      }[logLevel] || console.log;

    // Use custom color from config if specified, otherwise use the default assigned color
    let displayColor = color; // Default to the auto-assigned color
    if (config?.color) {
      displayColor = config.color;
      // Validate color format (basic check for hex or named colors)
      if (
        !displayColor.startsWith("#") &&
        !CSS.supports?.("color", displayColor)
      ) {
        // If CSS.supports is not available or color is invalid, keep the original
        // For basic validation, just check if it starts with # or is a likely color name
        if (
          !/^#[0-9a-fA-F]{3,6}$/.test(displayColor) &&
          !/^[a-zA-Z]+$/.test(displayColor)
        ) {
          displayColor = color; // Fall back to auto-assigned color
        }
      }
    }

    // Get foreground color for message text if specified
    let foregroundColor = "inherit"; // Default to inherit
    if (config?.foreground) {
      foregroundColor = config.foreground;
      // Basic validation for foreground color
      if (
        !foregroundColor.startsWith("#") &&
        !CSS.supports?.("color", foregroundColor)
      ) {
        if (
          !/^#[0-9a-fA-F]{3,6}$/.test(foregroundColor) &&
          !/^[a-zA-Z]+$/.test(foregroundColor)
        ) {
          foregroundColor = "inherit"; // Fall back to inherit
        }
      }
    }

    // Calculate timestamp strings
    let timeStr = "";
    let perfStr = "";

    // Show actual time at beginning if enabled
    if (timeEnabled) {
      const date = new Date(now);
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const seconds = date.getSeconds().toString().padStart(2, "0");
      const ms = date.getMilliseconds().toString().padStart(3, "0");

      // Format time based on the specified format
      const timeFormat =
        typeof config?.time === "string" ? config.time : "medium";
      let formattedTime = "";

      switch (timeFormat) {
        case "short":
          // HH:MM:SS
          formattedTime = `${hours}:${minutes}:${seconds}`;
          break;

        case "medium":
          // HH:MM:SS.mmm (default)
          formattedTime = `${hours}:${minutes}:${seconds}.${ms}`;
          break;

        case "long":
          // YYYY-MM-DD HH:MM:SS.mmm
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const day = date.getDate().toString().padStart(2, "0");
          formattedTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`;
          break;

        case "iso":
          // ISO 8601 format
          formattedTime = date.toISOString();
          break;

        case "unix":
          // Unix timestamp
          formattedTime = date.getTime().toString();
          break;

        case "relative":
          // Time since app started - with consistent width formatting
          const sinceStart = now - appStartTime;

          // Determine the maximum unit we've reached
          const maxMins = Math.floor(sinceStart / 60000);
          const maxSecs = Math.floor(sinceStart / 1000);

          if (maxMins > 0) {
            // Once we hit minutes, always show Mm SSs format (minutes can grow indefinitely)
            const mins = maxMins;
            const secs = Math.floor((sinceStart % 60000) / 1000);
            formattedTime = `${mins}m ${secs.toString().padStart(2, "0")}s`;
          } else if (maxSecs > 0) {
            // Seconds with decimal (SS.Ds)
            const secs = maxSecs;
            const ms = sinceStart % 1000;
            formattedTime = `${secs.toString().padStart(2, " ")}.${Math.floor(ms / 100)}s`;
          } else {
            // Milliseconds (MMMms)
            formattedTime = `${sinceStart.toString().padStart(3, " ")}ms`;
          }
          break;

        default:
          // Default to medium format
          formattedTime = `${hours}:${minutes}:${seconds}.${ms}`;
      }

      timeStr = `%c[${formattedTime}] `;
    }

    // Calculate elapsed time for performance timestamp
    if (perfEnabled) {
      const lastTime = lastMessageTime.get(namespace) || now;
      const elapsed = now - lastTime;

      // Format like debug library: +Xms, +Xs, +Xm, etc
      if (elapsed < 1000) {
        perfStr = ` +${elapsed}ms`;
      } else if (elapsed < 60000) {
        perfStr = ` +${Math.floor(elapsed / 1000)}s`;
      } else if (elapsed < 3600000) {
        perfStr = ` +${Math.floor(elapsed / 60000)}m`;
      } else {
        perfStr = ` +${Math.floor(elapsed / 3600000)}h`;
      }

      lastMessageTime.set(namespace, now);
    }

    // Process printf-style format strings for console output
    // The browser console doesn't understand %.4f, so we need to replace them
    let consoleFormat = actualFormat;
    const consoleArgs: any[] = [];
    let argIndex = 0;

    // Replace printf-style format specifiers with actual values
    consoleFormat = consoleFormat.replace(
      /%(?:\.(\d+))?[sdifjoO%]/g,
      (match, precision) => {
        if (match === "%%") return "%"; // Escaped %

        const arg = actualArgs[argIndex++];

        // Handle precision for floats and decimals
        if (
          precision &&
          (match.includes("f") || match.includes("d") || match.includes("i"))
        ) {
          if (typeof arg === "number") {
            return arg.toFixed(parseInt(precision));
          }
        }

        // For regular format specifiers that console understands, keep them
        if (
          match === "%s" ||
          match === "%d" ||
          match === "%i" ||
          match === "%o" ||
          match === "%O"
        ) {
          consoleArgs.push(arg);
          return match;
        }

        // For %f without precision or %j, just convert to string
        if (match === "%f") {
          return String(arg);
        }
        if (match === "%j" || match.includes("j")) {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }

        return String(arg);
      },
    );

    // Add remaining args that weren't consumed by format specifiers
    consoleArgs.push(...actualArgs.slice(argIndex));

    // Count format specifiers that remain in consoleFormat for proper arg slicing
    const formatSpecifierCount = (
      consoleFormat.match(/%[sdioO%]/g) || []
    ).filter((spec) => spec !== "%%").length;

    // Parse collapse config - can be boolean (use default limit) or number (custom limit)
    let shouldCollapse = false;
    let collapseLimit = DEFAULT_COLLAPSE_LIMIT;
    if (config?.collapse !== undefined) {
      if (typeof config.collapse === "boolean") {
        shouldCollapse = config.collapse;
        collapseLimit = DEFAULT_COLLAPSE_LIMIT;
      } else if (typeof config.collapse === "number") {
        shouldCollapse = true;
        collapseLimit = config.collapse;
      }
    }
    // Helper to format collapsed objects
    const formatCollapsedObject = (obj: any): string => {
      try {
        let processedObj = obj;
        // Apply precision if configured
        if (config?.precision !== undefined) {
          const precision = config.precision;
          const roundNumbers = (o: any): any => {
            if (typeof o === "number") {
              return (
                Math.round(o * Math.pow(10, precision)) /
                Math.pow(10, precision)
              );
            } else if (Array.isArray(o)) {
              return o.map(roundNumbers);
            } else if (o && typeof o === "object" && o.constructor === Object) {
              const rounded: any = {};
              for (const [k, v] of Object.entries(o)) {
                rounded[k] = roundNumbers(v);
              }
              return rounded;
            }
            return o;
          };
          processedObj = roundNumbers(obj);
        }

        // Create a concise string representation
        let str = JSON.stringify(processedObj);

        // Remove quotes from keys to save space
        str = str.replace(/"([^"]+)":/g, "$1:");

        // Truncate if still too long
        if (str.length > collapseLimit) {
          str = str.substring(0, collapseLimit) + "...}";
        }

        return str;
      } catch {
        return "{...}";
      }
    };

    // Parse stack config - can be boolean, string, or number
    const stackConfig = config?.stack ?? false;
    let showStack = stackConfig !== false;
    let stackDepth = config?.stackDepth ?? 10;
    let stackAll = config?.stackAll ?? false; // Include all frames except debugx
    let sourcemapEnabled = config?.stackMap ?? false; // Can be overridden by 'map' keyword
    let linkEnabled = config?.stackLink ?? false; // Can be overridden by 'link' keyword

    // Parse stack formats:
    // - true/false: enable/disable
    // - 'collapsed' or 'hide': collapsed stack
    // - 'auto': auto-expand every 10s
    // - 'auto 5': auto-expand with depth 5
    // - 'auto 5 all': auto-expand with depth 5, show all frames
    // - 'auto 5 all map link': with source maps and VSCode links
    // - 'collapsed 3': collapsed with depth 3
    // - 'collapsed all map link': collapsed, all frames, source maps, VSCode links
    // - number or '5': depth 5 expanded
    // - '5 all map link': depth 5 with all frames, source maps, and links
    let stackCollapsed = false;
    let isAutoStack = false;

    if (typeof stackConfig === "number") {
      // stack: 5 means depth 5
      stackDepth = stackConfig;
      showStack = true;
    } else if (typeof stackConfig === "string") {
      const parts = stackConfig.split(/\s+/);
      const mode = parts[0].toLowerCase();

      // Check for keywords anywhere in the string
      if (parts.includes("all")) {
        stackAll = true;
      }
      if (parts.includes("map")) {
        sourcemapEnabled = true;
      }
      if (parts.includes("link")) {
        linkEnabled = true;
      }

      if (mode === "collapsed" || mode === "hide") {
        stackCollapsed = true;
        // Look for depth number (skip keywords)
        for (let i = 1; i < parts.length; i++) {
          if (parts[i] !== "all" && parts[i] !== "map" && parts[i] !== "link") {
            const depth = parseInt(parts[i]);
            if (!isNaN(depth)) {
              stackDepth = depth;
              break;
            }
          }
        }
      } else if (mode === "auto") {
        isAutoStack = true;
        // Look for depth number (skip keywords)
        for (let i = 1; i < parts.length; i++) {
          if (parts[i] !== "all" && parts[i] !== "map" && parts[i] !== "link") {
            const depth = parseInt(parts[i]);
            if (!isNaN(depth)) {
              stackDepth = depth;
              break;
            }
          }
        }
      } else {
        // Try parsing as a number (e.g., '5' or '5 all')
        const depth = parseInt(mode);
        if (!isNaN(depth)) {
          stackDepth = depth;
          showStack = true;
        }
      }
    }
    if (isAutoStack) {
      // Auto mode: expand once every 10 seconds per namespace
      const lastExpanded = stackAutoExpandMap.get(namespace) || 0;
      const timeSinceExpanded = now - lastExpanded;
      if (timeSinceExpanded > 10000) {
        // 10 seconds
        stackCollapsed = false; // Expand (but don't set timestamp yet - wait until we actually show it)
      } else {
        stackCollapsed = true; // Collapse
      }
    }

    // Check if we're currently throttling
    let isCurrentlyThrottling = false;

    // Handle unique-throttle mode
    if (uniqueThrottle) {
      // Throttle per unique template message
      const uniqueKey = `${namespace}:${actualFormat}`;
      if (!uniqueThrottleMap.has(uniqueKey)) {
        uniqueThrottleMap.set(uniqueKey, {
          timestamps: [],
          suppressedCount: 0,
          lastShownTime: 0,
        });
      }

      const throttleInfo = uniqueThrottleMap.get(uniqueKey)!;

      // Check if enough time has passed since last message for this unique template
      if (
        throttleInfo.lastShownTime &&
        now - throttleInfo.lastShownTime < throttleCooldown
      ) {
        // Suppress this message but save it for trailing
        throttleInfo.suppressedCount++;
        const capturedStack = showStack ? new Error().stack : undefined;
        throttleInfo.lastMessage = {
          format: actualFormat,
          args: actualArgs,
          capturedStack,
        };

        // Clear existing timeout and set new one for trailing edge
        if (throttleInfo.trailingTimeout) {
          clearTimeout(throttleInfo.trailingTimeout as any);
        }

        // Set timeout to show the last message after cooldown
        const remainingTime =
          throttleCooldown - (now - throttleInfo.lastShownTime);
        throttleInfo.trailingTimeout = setTimeout(() => {
          if (throttleInfo.lastMessage && throttleInfo.suppressedCount > 0) {
            const savedKey = uniqueKey;
            const savedInfo = uniqueThrottleMap.get(savedKey);
            if (savedInfo) {
              const {
                format: lastFormat,
                args: lastArgs,
                capturedStack,
              } = savedInfo.lastMessage!;
              // Show the trailing message (DebugLib will show the suppressed count)
              const trailingDebug = DebugLib(namespace, config);
              if (capturedStack) {
                trailingDebug(
                  { __capturedStack: capturedStack },
                  lastFormat,
                  ...lastArgs,
                );
              } else {
                trailingDebug(lastFormat, ...lastArgs);
              }
              // Reset AFTER calling DebugLib so it can see the count
              savedInfo.lastShownTime = Date.now();
              savedInfo.suppressedCount = 0;
              savedInfo.trailingTimeout = undefined;
              savedInfo.lastMessage = undefined;
            }
          }
        }, remainingTime);

        return;
      }

      // Will show this message
      if (throttleInfo.suppressedCount > 0) {
        isCurrentlyThrottling = true;
      }
    }
    // Handle always-throttle mode
    if (alwaysThrottle) {
      // Always enforce throttle cooldown
      if (!autoThrottleMap.has(namespace)) {
        autoThrottleMap.set(namespace, {
          timestamps: [],
          suppressedCount: 0,
          lastShownTime: 0,
        });
      }

      const throttleInfo = autoThrottleMap.get(namespace)!;

      // Check if enough time has passed since last message
      if (
        throttleInfo.lastShownTime &&
        now - throttleInfo.lastShownTime < throttleCooldown
      ) {
        // Suppress this message but save it for trailing
        throttleInfo.suppressedCount++;
        // CAPTURE THE FUCKING STACK RIGHT NOW
        const capturedStack = showStack ? new Error().stack : undefined;
        throttleInfo.lastMessage = {
          format: actualFormat,
          args: actualArgs,
          capturedStack,
        };

        // Clear existing timeout and set new one for trailing edge
        if (throttleInfo.trailingTimeout) {
          clearTimeout(throttleInfo.trailingTimeout as any);
        }

        // Set timeout to show the last message after cooldown
        const remainingTime =
          throttleCooldown - (now - throttleInfo.lastShownTime);
        throttleInfo.trailingTimeout = setTimeout(() => {
          if (throttleInfo.lastMessage && throttleInfo.suppressedCount > 0) {
            const savedNamespace = namespace;
            const savedInfo = autoThrottleMap.get(savedNamespace);
            if (savedInfo) {
              const {
                format: lastFormat,
                args: lastArgs,
                capturedStack,
              } = savedInfo.lastMessage!;
              // Show the trailing message (DebugLib will show the suppressed count)
              // Pass the captured stack via inline config
              const trailingDebug = DebugLib(savedNamespace, config);
              if (capturedStack) {
                trailingDebug(
                  { __capturedStack: capturedStack },
                  lastFormat,
                  ...lastArgs,
                );
              } else {
                trailingDebug(lastFormat, ...lastArgs);
              }
              // Reset AFTER calling DebugLib so it can see the count
              savedInfo.lastShownTime = Date.now();
              savedInfo.suppressedCount = 0;
              savedInfo.trailingTimeout = undefined;
              savedInfo.lastMessage = undefined;
            }
          }
        }, remainingTime);

        return;
      }

      // Will show this message
      if (throttleInfo.suppressedCount > 0) {
        isCurrentlyThrottling = true;
      }
    }
    // Check auto-throttle mode
    else if (autoThrottleEnabled) {
      // Get or initialize throttle info
      if (!autoThrottleMap.has(namespace)) {
        autoThrottleMap.set(namespace, {
          timestamps: [],
          suppressedCount: 0,
          lastShownTime: 0,
        });
      }

      const throttleInfo = autoThrottleMap.get(namespace)!;
      const throttleWindow = config?.throttleWindow ?? DEFAULT_THROTTLE_WINDOW;
      const throttleThreshold =
        config?.throttleThreshold ?? DEFAULT_THROTTLE_THRESHOLD;

      // Clean old timestamps and add current one to check rate INCLUDING this message
      throttleInfo.timestamps = throttleInfo.timestamps.filter(
        (t) => now - t < throttleWindow,
      );

      // Check if we WILL BE throttling (including current message)
      isCurrentlyThrottling =
        throttleInfo.timestamps.length >= throttleThreshold - 1;

      // Also check if we have suppressed messages (we're actively throttling)
      if (!isCurrentlyThrottling && throttleInfo.suppressedCount > 0) {
        isCurrentlyThrottling = true;
      }
    }

    // Apply precision to all arguments if specified
    let precisionProcessedArgs = actualArgs;
    if (config?.precision !== undefined) {
      const precision = config.precision;
      const roundNumbers = (obj: any, depth: number = 0): any => {
        // Process up to 3 levels deep (0, 1, 2)
        if (depth > 2) return obj;

        if (typeof obj === "number") {
          return (
            Math.round(obj * Math.pow(10, precision)) / Math.pow(10, precision)
          );
        } else if (Array.isArray(obj)) {
          // Create a NEW array with rounded values
          const newArray = obj.map((item) => roundNumbers(item, depth + 1));
          return newArray;
        } else if (
          obj &&
          typeof obj === "object" &&
          obj.constructor === Object
        ) {
          // Create a NEW object with rounded values
          const rounded: any = {};
          for (const [k, v] of Object.entries(obj)) {
            rounded[k] = roundNumbers(v, depth + 1);
          }
          return rounded;
        }
        return obj;
      };

      precisionProcessedArgs = actualArgs.map((arg) => roundNumbers(arg, 0));
    }

    // Process args to handle object collapsing
    const processedArgs = shouldCollapse
      ? precisionProcessedArgs.map((arg) => {
          if (arg && typeof arg === "object") {
            // Handle null
            if (arg === null) return arg;

            // Check if object should be collapsed based on size
            try {
              const str = JSON.stringify(arg);

              // If object is small enough, return it as-is for proper console display
              if (str.length <= collapseLimit) {
                return arg; // Return the actual object, not a string!
              }

              // For long objects, use the collapsed format
              return formatCollapsedObject(arg);
            } catch (e) {
              // Fallback for circular references or other issues
              const className = arg.constructor?.name || "Object";
              return `[${className}]`;
            }
          }
          return arg;
        })
      : precisionProcessedArgs; // When not collapsing, just pass through the precision-processed args

    // Capture and format stack trace if enabled
    let stackTrace = "";
    if (showStack) {
      // Use captured stack if available (from throttling), otherwise generate new
      const stack = capturedStack || new Error().stack;
      if (stack) {
        const lines = stack.split("\n");

        // Use the parsed linkEnabled value which may have been set by 'link' keyword
        const enableLinks = linkEnabled;

        // Use the parsed sourcemapEnabled value which may have been set by 'map' keyword
        const useSourceMaps = sourcemapEnabled;

        // Determine source map type (use config value if it's a string type specifier)
        let sourcemapType: "auto" | "inline" | "external" | "eval" = "auto";
        if (typeof config?.stackMap === "string") {
          sourcemapType = config.stackMap as
            | "auto"
            | "inline"
            | "external"
            | "eval";
        }

        // Skip first line (Error message) and find where our debug code ends
        // We need to skip the internal debugx frames but keep the first user frame
        let startIndex = 1;
        let foundDebugx = false;

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i];

          // Check if this is a debugx internal frame
          const isDebugxInternal =
            line.includes("debugx.ts") ||
            line.includes("debugx.js") ||
            line.includes("at DebugLib") ||
            line.includes("at createDebug") ||
            line.includes("at normalDebug") ||
            line.includes("at throttled") ||
            line.includes("at enhanced");

          if (isDebugxInternal) {
            foundDebugx = true;
          } else if (foundDebugx) {
            // First non-debugx frame after seeing debugx frames
            startIndex = i;
            break;
          }
        }

        // Get lines for processing
        const potentialLines = lines.slice(
          startIndex,
          startIndex + stackDepth * 2,
        ); // Get extra to account for filtering

        // Filter and format the stack trace
        const formattedLines: string[] = [];
        let userCodeFrames = 0;

        // Check if we have a manual line number specified (line, l, or L shorthand)
        const manualLine =
          config?.line || (config as any)?.l || (config as any)?.L;

        for (let line of potentialLines) {
          // Stop if we've shown enough user code frames
          if (userCodeFrames >= stackDepth) break;

          // Clean up the stack line
          line = line.trim();

          // Parse different stack trace formats
          // Chrome with eval-source-map: "at functionName (webpack-internal:///./src/file.tsx:10:20)"
          // Chrome with source-map: "at functionName (http://localhost:3000/src/file.tsx:10:20)"
          // Chrome bundled: "at functionName (file.js:10:20)"
          // Firefox: "functionName@file:line:col"

          let funcName = "";
          let location = "";

          // Match Chrome format with "at"
          const atMatch = line.match(/^at\s+(.+?)\s+\((.+)\)$/);
          if (atMatch) {
            funcName = atMatch[1];
            location = atMatch[2];
          } else if (line.includes("@")) {
            // Firefox format
            const parts = line.split("@");
            funcName = parts[0];
            location = parts[1] || "";
          } else if (line.startsWith("at ")) {
            // Just location, no function name
            location = line.substring(3).trim();
            funcName = "";
          } else {
            location = line;
          }

          // Clean up function names
          if (
            !funcName ||
            funcName === "anonymous" ||
            funcName === "<anonymous>" ||
            funcName === "eval"
          ) {
            funcName = "";
          } else if (funcName.includes(".")) {
            // Extract method name from "Class.method" or "Object.method"
            const parts = funcName.split(".");
            const last = parts[parts.length - 1];
            const secondLast = parts[parts.length - 2];
            // If it's Object.<anonymous> or similar, skip
            if (last === "<anonymous>" || secondLast === "Object") {
              funcName = "";
            } else {
              funcName = last;
            }
          }

          // Extract location information
          let shortLocation = "";
          let resolvedSourcePath = ""; // Track the resolved source file path for VSCode links

          // Handle webpack-internal:/// format from eval-source-map
          if (location.includes("webpack-internal:///")) {
            const internalMatch = location.match(
              /webpack-internal:\/\/\/\.?\/?(.+?):(\d+):(\d+)/,
            );
            if (internalMatch) {
              let path = internalMatch[1];
              let lineNum = parseInt(internalMatch[2]);

              // Use manual line number for the first user frame if provided
              if (manualLine && userCodeFrames === 0) {
                lineNum = manualLine;
              }

              // If source maps are enabled, try to fetch and parse the source map
              if (useSourceMaps && !manualLine) {
                try {
                  let sourceMapData = null;
                  const colNum = parseInt(internalMatch[3]);

                  // Based on source map type, try different approaches
                  if (sourcemapType === "auto") {
                    // Auto-detect based on what's available
                    // First try external .map file
                    const scriptUrl = window.location.origin + "/main.js"; // Adjust based on your bundle
                    sourceMapData = sourceMapsCache.get(scriptUrl);

                    if (!sourceMapData) {
                      const mapUrl = scriptUrl + ".map";
                      const xhr = new XMLHttpRequest();
                      xhr.open("GET", mapUrl, false);
                      try {
                        xhr.send();
                        if (xhr.status === 200) {
                          sourceMapData = JSON.parse(xhr.responseText);
                          sourceMapsCache.set(scriptUrl, sourceMapData);
                        }
                      } catch (e) {
                        // External map not available, might be inline
                      }
                    }
                  } else if (sourcemapType === "external") {
                    // Only try external .map file
                    const scriptUrl = window.location.origin + "/main.js";
                    sourceMapData = sourceMapsCache.get(scriptUrl);

                    if (!sourceMapData) {
                      const mapUrl = scriptUrl + ".map";
                      const xhr = new XMLHttpRequest();
                      xhr.open("GET", mapUrl, false);
                      xhr.send();
                      if (xhr.status === 200) {
                        sourceMapData = JSON.parse(xhr.responseText);
                        sourceMapsCache.set(scriptUrl, sourceMapData);
                      }
                    }
                  } else if (sourcemapType === "inline") {
                    // Try to extract inline source map from current script
                    // This would need access to the script content
                    console.warn("Inline source maps not yet supported");
                  } else if (sourcemapType === "eval") {
                    // eval-source-map embeds maps in eval strings
                    // Browser should already handle these
                    console.log("Using browser eval-source-map resolution");
                  }

                  // If we have source map data, try to resolve the original position
                  if (sourceMapData && sourceMapData.mappings) {
                    // Parse the mappings if not already parsed
                    const mapKey = window.location.origin + "/main.js";
                    let mappings = sourceMapsCache.get(mapKey + ":mappings");
                    if (!mappings) {
                      mappings = parseMappings(
                        sourceMapData.mappings,
                        sourceMapData.sources || [],
                      );
                      sourceMapsCache.set(mapKey + ":mappings", mappings);
                    }

                    // Look up the original position
                    // Try exact match first, then nearby positions
                    let mapping = mappings.get(`${lineNum}:${colNum}`);
                    if (!mapping) {
                      // Try without column or with column 0
                      for (let col = colNum; col >= 0 && !mapping; col--) {
                        mapping = mappings.get(`${lineNum}:${col}`);
                      }
                    }

                    if (mapping) {
                      // Found a mapping!
                      lineNum = mapping.originalLine;
                      path = mapping.source
                        .replace(/^webpack:\/\/\//, "")
                        .replace(/^\.\//, "");
                      console.log(`Resolved to original: ${path}:${lineNum}`);
                    } else {
                      console.log(`No mapping found for ${lineNum}:${colNum}`);
                    }
                  }
                } catch (e) {
                  console.warn("Source map parsing failed:", e);
                }
              }

              // Note: webpack eval-source-map line numbers are approximate
              // The offset varies throughout the file due to module wrapping and transformations
              // Typically off by 2-10 lines depending on location in the file

              // Clean up the path - remove leading ./ or src/
              path = path.replace(/^\.\//, "").replace(/^src\//, "");

              // Extract just the filename
              const parts = path.split("/");
              const filename = parts[parts.length - 1];

              // Add ~ prefix to indicate approximate line number (unless manual or source mapped)
              const linePrefix =
                (manualLine && userCodeFrames === 0) || useSourceMaps
                  ? ""
                  : "~";
              shortLocation = `${filename}:${linePrefix}${lineNum}`;
            }
          }

          // Handle regular source-mapped files (TypeScript/JSX files, not bundled JS)
          if (!shortLocation) {
            const sourceMappedMatch = location.match(
              /([^/\\(]+\.(tsx|ts|jsx|mjs|cjs)):(\d+):(\d+)/,
            );
            if (sourceMappedMatch) {
              shortLocation = `${sourceMappedMatch[1]}:${sourceMappedMatch[3]}`;
            }
          }

          // Handle webpack eval strings
          if (!shortLocation && location.includes("eval")) {
            const evalMatch = location.match(/eval\s+at\s+.+?\s+\((.+?)\)/);
            if (evalMatch) {
              location = evalMatch[1];
              // Try to extract from the eval location
              const fileMatch = location.match(
                /([^/\\]+\.(tsx?|jsx?)):(\d+):(\d+)/,
              );
              if (fileMatch) {
                shortLocation = `${fileMatch[1]}:${fileMatch[2]}`;
              }
            }
          }

          // Fallback to bundled files
          if (!shortLocation) {
            // Try to match either full URL or just bundle name
            const bundleMatch = location.match(
              /^(?:(https?:\/\/[^/]+\/))?([^/]+\.js):(\d+):(\d+)$/,
            );
            if (bundleMatch) {
              const baseUrl =
                bundleMatch[1] ||
                (typeof window !== "undefined"
                  ? window.location.origin + "/"
                  : "");
              const originalBundleName = bundleMatch[2];
              let bundleName = originalBundleName;
              const lineNum = parseInt(bundleMatch[3]);
              const colNum = parseInt(bundleMatch[4]);

              // Try to resolve through source maps if enabled
              if (useSourceMaps && baseUrl) {
                try {
                  const bundleUrl = baseUrl + originalBundleName;
                  let sourceMapData = sourceMapsCache.get(bundleUrl);

                  if (!sourceMapData) {
                    // Try to fetch the source map
                    const mapUrl = bundleUrl + ".map";
                    const xhr = new XMLHttpRequest();
                    xhr.open("GET", mapUrl, false);
                    try {
                      xhr.send();
                      if (xhr.status === 200) {
                        sourceMapData = JSON.parse(xhr.responseText);
                        sourceMapsCache.set(bundleUrl, sourceMapData);

                        // Parse mappings
                        if (sourceMapData.mappings) {
                          const mappings = parseMappings(
                            sourceMapData.mappings,
                            sourceMapData.sources || [],
                          );
                          sourceMapsCache.set(
                            bundleUrl + ":mappings",
                            mappings,
                          );
                        }
                      }
                    } catch (e) {
                      // Source map not available
                    }
                  }

                  // Look up the mapping
                  if (sourceMapData) {
                    let mappings = sourceMapsCache.get(bundleUrl + ":mappings");
                    if (mappings) {
                      // Try to find mapping for this position
                      let mapping = mappings.get(`${lineNum}:${colNum}`);
                      if (!mapping) {
                        // Try columns before and after
                        for (
                          let offset = 1;
                          offset <= 50 && !mapping;
                          offset++
                        ) {
                          mapping =
                            mappings.get(`${lineNum}:${colNum - offset}`) ||
                            mappings.get(`${lineNum}:${colNum + offset}`);
                        }
                      }
                      if (!mapping) {
                        // Try column 0 as last resort
                        mapping =
                          mappings.get(`${lineNum}:0`) ||
                          mappings.get(`${lineNum}:1`);
                      }

                      if (mapping) {
                        // Found original source!
                        // Source paths come in format: "webpack://bike-dash/./client/src/beta/App.tsx"
                        let sourcePath = mapping.source;

                        // Remove webpack protocol and project name
                        sourcePath = sourcePath.replace(
                          /^webpack:\/\/[^/]+\//,
                          "",
                        );
                        // Remove leading ./
                        sourcePath = sourcePath.replace(/^\.\//, "");

                        const sourceFile = sourcePath.split("/").pop();
                        shortLocation = `${sourceFile}:${mapping.originalLine}`;
                        // Save the clean source path for VSCode link generation
                        resolvedSourcePath = sourcePath;
                      } else {
                        // No mapping found - just use the full bundle name with line number
                        // DON'T SHORTEN IT!
                        shortLocation = `${bundleName}:${lineNum}`;
                      }
                    } else {
                      // Couldn't parse mappings - use full bundle name
                      shortLocation = `${bundleName}:${lineNum}`;
                    }
                  } else {
                    // No source map available - use full bundle name
                    shortLocation = `${bundleName}:${lineNum}`;
                  }
                } catch (e) {
                  // Failed to resolve - use full bundle name
                  shortLocation = `${bundleName}:${lineNum}`;
                }
              } else {
                // Source maps not enabled - use full bundle name
                shortLocation = `${bundleName}:${lineNum}`;
              }
            }
          }

          // Last fallback - just show whatever we have
          if (!shortLocation) {
            const fileMatch = location.match(/([^/\\]+)$/);
            shortLocation = fileMatch ? fileMatch[1] : location;
          }

          // Never filter out the first frame - it's the actual call site
          const isFirstFrame = userCodeFrames === 0;

          // Skip ALL debugx internals - they're never useful in the stack trace
          const isDebugxFrame =
            shortLocation.includes("debugx.ts:") ||
            shortLocation.includes("debugx.js:") ||
            (resolvedSourcePath && resolvedSourcePath.includes("debugx.ts")) ||
            (resolvedSourcePath && resolvedSourcePath.includes("debugx.js"));

          // Skip webpack internals and bundler artifacts (but not for first frame, unless stackAll)
          const isWebpackInternal =
            !stackAll &&
            !isFirstFrame &&
            (shortLocation.includes("__webpack_require__") ||
              funcName === "__webpack_require__" ||
              funcName === "fn" ||
              location.includes("__webpack_require__") ||
              location.includes("webpack/bootstrap") ||
              shortLocation.includes("startup:") || // Skip webpack startup frames
              // Skip unmapped bundle lines at the end of the bundle (webpack runtime)
              (shortLocation.match(/\.js:\d+$/) &&
                parseInt(shortLocation.match(/:(\d+)$/)?.[1] || "0") > 30000));

          // Skip node_modules frames (but not for first frame, unless stackAll)
          const isNodeModules =
            !stackAll &&
            !isFirstFrame &&
            (shortLocation.includes("node_modules/") ||
              resolvedSourcePath.includes("node_modules/") ||
              location.includes("node_modules/"));

          // Skip entry points that aren't useful (but not for first frame, unless stackAll)
          const isEntryPoint =
            !stackAll &&
            !isFirstFrame &&
            shortLocation.includes("entry.tsx") &&
            !funcName;

          // Only include frames based on filters
          // Always skip debugx frames, but include everything else if stackAll is true
          if (
            !isDebugxFrame &&
            (stackAll ||
              (!isWebpackInternal && !isNodeModules && !isEntryPoint))
          ) {
            userCodeFrames++;

            // Format with or without function name
            let formatted: string;

            // Add VSCode link if enabled
            if (enableLinks && shortLocation) {
              // Extract filename and line number
              const locationMatch = shortLocation.match(/^(.+?):(~?)(\d+)$/);
              if (locationMatch) {
                // Use the line number from shortLocation (which may be manual for first frame)
                const lineNum = locationMatch[3];
                let fullPath = "";

                // Try to get the full path
                if (resolvedSourcePath) {
                  // We have a resolved source path from source map
                  const projectRoot = "/Users/brantwedel/bitbased/bike-dash/";

                  // The resolved path should already be like "client/src/beta/App.tsx"
                  // or could be "webpack/startup" for webpack internals
                  if (resolvedSourcePath.startsWith("webpack/")) {
                    // Skip VSCode links for webpack internals
                    fullPath = "";
                  } else if (resolvedSourcePath.startsWith("/")) {
                    // Absolute path, use as is
                    fullPath = resolvedSourcePath;
                  } else {
                    // Relative path from project root
                    fullPath = projectRoot + resolvedSourcePath;
                  }
                } else if (location.includes("webpack-internal:///./")) {
                  // Extract the relative path from webpack-internal
                  const pathMatch = location.match(
                    /webpack-internal:\/\/\/\.\/(.+?):/,
                  );
                  if (pathMatch) {
                    const projectRoot = "/Users/brantwedel/bitbased/bike-dash/";
                    fullPath = projectRoot + pathMatch[1];
                  }
                }

                if (fullPath) {
                  // Create a clickable VSCode URI link
                  // MUST escape the colon or Chrome will strip everything after it thinking it's a port
                  // URL encode just the path and line separator
                  const vscodeUri = `vscode://file${fullPath.replace(/:/g, "%3A")}%3A${lineNum}`;

                  // Output as a clickable link using console's URL detection
                  if (funcName && funcName !== "") {
                    formatted = `    ↳ ${funcName}() at ${shortLocation}\n         ${vscodeUri}`;
                  } else {
                    formatted = `    ↳ at ${shortLocation}\n         ${vscodeUri}`;
                  }
                } else {
                  // Fallback to non-link format
                  if (funcName && funcName !== "") {
                    formatted = `    ↳ ${funcName}() at ${shortLocation}`;
                  } else {
                    formatted = `    ↳ at ${shortLocation}`;
                  }
                }
              } else {
                // Can't parse location, use non-link format
                if (funcName && funcName !== "") {
                  formatted = `    ↳ ${funcName}() at ${shortLocation}`;
                } else {
                  formatted = `    ↳ at ${shortLocation}`;
                }
              }
            } else {
              // Links not enabled, use regular format
              if (funcName && funcName !== "") {
                formatted = `    ↳ ${funcName}() at ${shortLocation}`;
              } else {
                formatted = `    ↳ at ${shortLocation}`;
              }
            }

            formattedLines.push(formatted);
          }
        }

        if (formattedLines.length > 0) {
          // Keep the natural order - call site first, then callers
          stackTrace = "\n" + formattedLines.join("\n");
        }
      }
    }

    // Handle unique throttle display logic - this handles ALL unique throttling display
    if (uniqueThrottle) {
      const uniqueKey = `${namespace}:${actualFormat}`;
      const throttleInfo = uniqueThrottleMap.get(uniqueKey);
      const suppressedCount = throttleInfo?.suppressedCount || 0;

      const padding = alignEnabled
        ? " ".repeat(Math.max(0, longestNamespace - namespace.length))
        : "";

      // Call hooks for filtered console output
      const filteredEntry: DebugLogEntry = {
        namespace,
        level: logLevel,
        message:
          (suppressedCount ? `[${suppressedCount}] ` : "") +
          formatMessage(
            actualFormat,
            processedArgs,
            config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
          ),
        template: actualFormat,
        formattedMessage: formatMessageToHtml(
          (suppressedCount ? `[${suppressedCount}] ` : "") +
            formatMessage(
              actualFormat,
              processedArgs,
              config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
            ),
          processedArgs,
          namespace,
          displayColor,
          config?.foreground,
          config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
          config,
          perfStr,
          suppressedCount,
          timeStr,
          stackTrace,
          stackCollapsed,
        ),
        args: processedArgs,
        timestamp: now,
      };
      callFilteredHooks(filteredEntry);

      if (suppressedCount > 0) {
        // Display with suppressed count
        const countStr = `${suppressedCount}`;
        const countPadding = " ".repeat(Math.max(0, 3 - countStr.length));

        if (stackTrace) {
          const groupFn = stackCollapsed
            ? console.groupCollapsed
            : console.group;
          groupFn(
            `${timeStr}%c${namespace}%c${padding} %c[${countStr}]${countPadding} %c${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
            ...(timeStr ? ["color: gray; font-weight: normal"] : []),
            `color: ${displayColor}; font-weight: bold`,
            "color: inherit; font-weight: normal",
            "color: gray; font-weight: normal",
            `color: ${foregroundColor}; font-weight: normal`,
            ...consoleArgs.slice(0, formatSpecifierCount),
            ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
            ...consoleArgs.slice(formatSpecifierCount),
          );
          console.log(stackTrace.trim());
          console.groupEnd();
          // Update auto-expand timestamp if we just showed an expanded stack
          if (isAutoStack && !stackCollapsed) {
            stackAutoExpandMap.set(namespace, now);
          }
        } else {
          consoleMethod(
            `${timeStr}%c${namespace}%c${padding} %c[${countStr}]${countPadding} %c${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
            ...(timeStr ? ["color: gray; font-weight: normal"] : []),
            `color: ${displayColor}; font-weight: bold`,
            "color: inherit; font-weight: normal",
            "color: gray; font-weight: normal",
            `color: ${foregroundColor}; font-weight: normal`,
            ...consoleArgs.slice(0, formatSpecifierCount),
            ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
            ...consoleArgs.slice(formatSpecifierCount),
          );
        }
      } else {
        // Display without suppressed count (normal display)
        if (stackTrace) {
          const groupFn = stackCollapsed
            ? console.groupCollapsed
            : console.group;
          groupFn(
            `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
            ...(timeStr ? ["color: gray; font-weight: normal"] : []),
            `color: ${displayColor}; font-weight: bold`,
            `color: ${foregroundColor}; font-weight: normal`,
            ...consoleArgs.slice(0, formatSpecifierCount),
            ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
            ...consoleArgs.slice(formatSpecifierCount),
          );
          console.log(stackTrace.trim());
          console.groupEnd();
          // Update auto-expand timestamp if we just showed an expanded stack
          if (isAutoStack && !stackCollapsed) {
            stackAutoExpandMap.set(namespace, now);
          }
        } else {
          consoleMethod(
            `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
            ...(timeStr ? ["color: gray; font-weight: normal"] : []),
            `color: ${displayColor}; font-weight: bold`,
            `color: ${foregroundColor}; font-weight: normal`,
            ...consoleArgs.slice(0, formatSpecifierCount),
            ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
            ...consoleArgs.slice(formatSpecifierCount),
          );
        }
      }

      // Update throttle info
      if (throttleInfo) {
        throttleInfo.lastShownTime = now;
        throttleInfo.suppressedCount = 0;
      }

      return; // Skip ALL other display logic for unique throttling
    }

    // Handle throttling display logic
    if (alwaysThrottle || autoThrottleEnabled) {
      // Get or create throttle info for this namespace
      if (!autoThrottleMap.has(namespace)) {
        autoThrottleMap.set(namespace, {
          timestamps: [],
          suppressedCount: 0,
          lastShownTime: 0,
        });
      }

      const throttleInfo = autoThrottleMap.get(namespace)!;

      if (alwaysThrottle) {
        // Already checked cooldown above and returned if suppressing
        // If we got here, we're showing the message
        const suppressedCount = throttleInfo.suppressedCount;
        const padding = alignEnabled
          ? " ".repeat(Math.max(0, longestNamespace - namespace.length))
          : "";

        if (suppressedCount > 0) {
          // Pad the suppressed count to 3 digits total (e.g., "[1]  ", "[10] ", "[999]")
          const countStr = `${suppressedCount}`;
          const countPadding = " ".repeat(Math.max(0, 3 - countStr.length)); // 3 chars for "999"

          // Call hooks for filtered console output
          const filteredEntry: DebugLogEntry = {
            namespace,
            level: logLevel,
            message:
              (suppressedCount ? `[${suppressedCount}] ` : "") +
              formatMessage(
                actualFormat,
                processedArgs,
                config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
              ),
            template: actualFormat,
            formattedMessage: formatMessageToHtml(
              (suppressedCount ? `[${suppressedCount}] ` : "") +
                formatMessage(
                  actualFormat,
                  processedArgs,
                  config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
                ),
              processedArgs,
              namespace,
              displayColor,
              config?.foreground,
              config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
              config,
              perfStr,
              suppressedCount,
              timeStr,
              stackTrace,
              stackCollapsed,
            ),
            args: processedArgs,
            timestamp: now,
          };
          callFilteredHooks(filteredEntry);

          if (stackTrace) {
            const groupFn = stackCollapsed
              ? console.groupCollapsed
              : console.group;
            groupFn(
              `${timeStr}%c${namespace}%c${padding} %c[${countStr}]${countPadding} %c${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
              ...(timeStr ? ["color: gray; font-weight: normal"] : []),
              `color: ${displayColor}; font-weight: bold`,
              "color: inherit; font-weight: normal",
              "color: gray; font-weight: normal",
              `color: ${foregroundColor}; font-weight: normal`,
              ...consoleArgs.slice(0, formatSpecifierCount),
              ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
              ...consoleArgs.slice(formatSpecifierCount),
            );
            console.log(stackTrace.trim());
            console.groupEnd();
            // Update auto-expand timestamp if we just showed an expanded stack
            if (isAutoStack && !stackCollapsed) {
              stackAutoExpandMap.set(namespace, now);
            }
          } else {
            consoleMethod(
              `${timeStr}%c${namespace}%c${padding} %c[${countStr}]${countPadding} %c${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
              ...(timeStr ? ["color: gray; font-weight: normal"] : []),
              `color: ${displayColor}; font-weight: bold`,
              "color: inherit; font-weight: normal",
              "color: gray; font-weight: normal",
              `color: ${foregroundColor}; font-weight: normal`,
              ...consoleArgs.slice(0, formatSpecifierCount),
              ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
              ...consoleArgs.slice(formatSpecifierCount),
            );
          }
        } else {
          // Call hooks for filtered console output (throttled without suppressed count)
          const filteredEntry: DebugLogEntry = {
            namespace,
            level: logLevel,
            message: formatMessage(
              actualFormat,
              processedArgs,
              config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
            ),
            template: actualFormat,
            formattedMessage: formatMessageToHtml(
              formatMessage(
                actualFormat,
                processedArgs,
                config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
              ),
              processedArgs,
              namespace,
              displayColor,
              config?.foreground,
              config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
              config,
              perfStr,
              0,
              timeStr,
              stackTrace,
              stackCollapsed,
            ),
            args: processedArgs,
            timestamp: now,
          };
          callFilteredHooks(filteredEntry);

          if (stackTrace) {
            const groupFn = stackCollapsed
              ? console.groupCollapsed
              : console.group;
            groupFn(
              `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
              ...(timeStr ? ["color: gray; font-weight: normal"] : []),
              `color: ${displayColor}; font-weight: bold`,
              `color: ${foregroundColor}; font-weight: normal`,
              ...consoleArgs.slice(0, formatSpecifierCount),
              ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
              ...consoleArgs.slice(formatSpecifierCount),
            );
            console.log(stackTrace.trim());
            console.groupEnd();
            // Update auto-expand timestamp if we just showed an expanded stack
            if (isAutoStack && !stackCollapsed) {
              stackAutoExpandMap.set(namespace, now);
            }
          } else {
            consoleMethod(
              `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
              ...(timeStr ? ["color: gray; font-weight: normal"] : []),
              `color: ${displayColor}; font-weight: bold`,
              `color: ${foregroundColor}; font-weight: normal`,
              ...consoleArgs.slice(0, formatSpecifierCount),
              ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
              ...consoleArgs.slice(formatSpecifierCount),
            );
          }
        }

        throttleInfo.lastShownTime = now;
        throttleInfo.suppressedCount = 0;
      } else {
        // Auto-throttle mode
        const throttleWindow =
          config?.throttleWindow ?? DEFAULT_THROTTLE_WINDOW;
        const throttleThreshold =
          config?.throttleThreshold ?? DEFAULT_THROTTLE_THRESHOLD;

        // Clean old timestamps outside the window
        throttleInfo.timestamps = throttleInfo.timestamps.filter(
          (t) => now - t < throttleWindow,
        );

        // Add current timestamp
        throttleInfo.timestamps.push(now);

        // Check if we're being spammed
        const isSpamming = throttleInfo.timestamps.length >= throttleThreshold;

        // Check if this is a trailing message with suppressed count
        const hasTrailingSuppressedCount =
          !isSpamming && throttleInfo.suppressedCount > 0;

        if (isSpamming || hasTrailingSuppressedCount) {
          // We're in throttle mode OR showing trailing message with count
          if (
            isSpamming &&
            now - throttleInfo.lastShownTime < throttleCooldown
          ) {
            // Suppress this message but save it for trailing
            throttleInfo.suppressedCount++;
            // CAPTURE THE FUCKING STACK RIGHT NOW
            const capturedStack = showStack ? new Error().stack : undefined;
            throttleInfo.lastMessage = {
              format: actualFormat,
              args: actualArgs,
              capturedStack,
            };

            // Clear existing timeout and set new one for trailing edge
            if (throttleInfo.trailingTimeout) {
              clearTimeout(throttleInfo.trailingTimeout as any);
            }

            // Set timeout to show the last message after cooldown
            const remainingTime =
              throttleCooldown - (now - throttleInfo.lastShownTime);
            throttleInfo.trailingTimeout = setTimeout(() => {
              if (
                throttleInfo.lastMessage &&
                throttleInfo.suppressedCount > 0
              ) {
                const savedNamespace = namespace;
                const savedInfo = autoThrottleMap.get(savedNamespace);
                if (savedInfo) {
                  const {
                    format: lastFormat,
                    args: lastArgs,
                    capturedStack,
                  } = savedInfo.lastMessage!;
                  // Show the trailing message (DebugLib will show the suppressed count)
                  // Pass the captured stack via inline config
                  const trailingDebug = DebugLib(savedNamespace, config);
                  if (capturedStack) {
                    trailingDebug(
                      { __capturedStack: capturedStack },
                      lastFormat,
                      ...lastArgs,
                    );
                  } else {
                    trailingDebug(lastFormat, ...lastArgs);
                  }
                  // Reset AFTER calling DebugLib so it can see the count
                  savedInfo.lastShownTime = Date.now();
                  savedInfo.suppressedCount = 0;
                  savedInfo.trailingTimeout = undefined;
                  savedInfo.lastMessage = undefined;
                }
              }
            }, remainingTime);

            return;
          } else {
            // Show this message with suppression count
            const suppressedCount = throttleInfo.suppressedCount;
            const padding = alignEnabled
              ? " ".repeat(Math.max(0, longestNamespace - namespace.length))
              : "";

            if (suppressedCount > 0) {
              // Pad the suppressed count to 3 digits total
              const countStr = `${suppressedCount}`;
              const countPadding = " ".repeat(Math.max(0, 3 - countStr.length));

              // Call hooks for filtered console output (auto-throttle with suppressed count)
              const filteredEntry: DebugLogEntry = {
                namespace,
                level: logLevel,
                message:
                  `[${suppressedCount}] ` +
                  formatMessage(
                    actualFormat,
                    processedArgs,
                    config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
                  ),
                template: actualFormat,
                formattedMessage: formatMessageToHtml(
                  `[${suppressedCount}] ` +
                    formatMessage(
                      actualFormat,
                      processedArgs,
                      config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
                    ),
                  processedArgs,
                  namespace,
                  displayColor,
                  config?.foreground,
                  config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
                  config,
                  perfStr,
                  suppressedCount,
                  timeStr,
                  stackTrace,
                  stackCollapsed,
                ),
                args: processedArgs,
                timestamp: now,
              };
              callFilteredHooks(filteredEntry);

              if (stackTrace) {
                const groupFn = stackCollapsed
                  ? console.groupCollapsed
                  : console.group;
                groupFn(
                  `${timeStr}%c${namespace}%c${padding} %c[${countStr}]${countPadding} %c${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
                  ...(timeStr ? ["color: gray; font-weight: normal"] : []),
                  `color: ${displayColor}; font-weight: bold`,
                  "color: inherit; font-weight: normal",
                  "color: gray; font-weight: normal",
                  `color: ${foregroundColor}; font-weight: normal`,
                  ...consoleArgs.slice(0, formatSpecifierCount),
                  ...(perfStr
                    ? [`color: ${displayColor}; font-weight: bold`]
                    : []),
                  ...consoleArgs.slice(formatSpecifierCount),
                );
                console.log(stackTrace.trim());
                console.groupEnd();
                // Update auto-expand timestamp if we just showed an expanded stack
                if (isAutoStack && !stackCollapsed) {
                  stackAutoExpandMap.set(namespace, now);
                }
              } else {
                consoleMethod(
                  `${timeStr}%c${namespace}%c${padding} %c[${countStr}]${countPadding} %c${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
                  ...(timeStr ? ["color: gray; font-weight: normal"] : []),
                  `color: ${displayColor}; font-weight: bold`,
                  "color: inherit; font-weight: normal",
                  "color: gray; font-weight: normal",
                  `color: ${foregroundColor}; font-weight: normal`,
                  ...consoleArgs.slice(0, formatSpecifierCount),
                  ...(perfStr
                    ? [`color: ${displayColor}; font-weight: bold`]
                    : []),
                  ...consoleArgs.slice(formatSpecifierCount),
                );
              }
            } else {
              // Call hooks for filtered console output (auto-throttle without suppressed count)
              const filteredEntry: DebugLogEntry = {
                namespace,
                level: logLevel,
                message: formatMessage(
                  actualFormat,
                  processedArgs,
                  config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
                ),
                template: actualFormat,
                formattedMessage: formatMessageToHtml(
                  formatMessage(
                    actualFormat,
                    processedArgs,
                    config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
                  ),
                  processedArgs,
                  namespace,
                  displayColor,
                  config?.foreground,
                  config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
                  config,
                  perfStr,
                  0,
                  timeStr,
                  stackTrace,
                  stackCollapsed,
                ),
                args: processedArgs,
                timestamp: now,
              };
              callFilteredHooks(filteredEntry);

              if (stackTrace) {
                const groupFn = stackCollapsed
                  ? console.groupCollapsed
                  : console.group;
                groupFn(
                  `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
                  ...(timeStr ? ["color: gray; font-weight: normal"] : []),
                  `color: ${displayColor}; font-weight: bold`,
                  `color: ${foregroundColor}; font-weight: normal`,
                  ...consoleArgs.slice(0, formatSpecifierCount),
                  ...(perfStr
                    ? [`color: ${displayColor}; font-weight: bold`]
                    : []),
                  ...consoleArgs.slice(formatSpecifierCount),
                );
                console.log(stackTrace.trim());
                console.groupEnd();
                // Update auto-expand timestamp if we just showed an expanded stack
                if (isAutoStack && !stackCollapsed) {
                  stackAutoExpandMap.set(namespace, now);
                }
              } else {
                consoleMethod(
                  `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
                  ...(timeStr ? ["color: gray; font-weight: normal"] : []),
                  `color: ${displayColor}; font-weight: bold`,
                  `color: ${foregroundColor}; font-weight: normal`,
                  ...consoleArgs.slice(0, formatSpecifierCount),
                  ...(perfStr
                    ? [`color: ${displayColor}; font-weight: bold`]
                    : []),
                  ...consoleArgs.slice(formatSpecifierCount),
                );
              }
            }

            throttleInfo.lastShownTime = now;
            throttleInfo.suppressedCount = 0;
          }
        } else {
          // Not spamming, show normally
          const padding = alignEnabled
            ? " ".repeat(Math.max(0, longestNamespace - namespace.length))
            : "";

          // Call hooks for filtered console output (auto-throttle not spamming)
          const filteredEntry: DebugLogEntry = {
            namespace,
            level: logLevel,
            message: formatMessage(
              actualFormat,
              processedArgs,
              config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
            ),
            template: actualFormat,
            formattedMessage: formatMessageToHtml(
              formatMessage(
                actualFormat,
                processedArgs,
                config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
              ),
              processedArgs,
              namespace,
              displayColor,
              config?.foreground,
              config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
              config,
              perfStr,
              0,
              timeStr,
              stackTrace,
              stackCollapsed,
            ),
            args: processedArgs,
            timestamp: now,
          };
          callFilteredHooks(filteredEntry);

          if (stackTrace) {
            const groupFn = stackCollapsed
              ? console.groupCollapsed
              : console.group;
            groupFn(
              `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
              ...(timeStr ? ["color: gray; font-weight: normal"] : []),
              `color: ${displayColor}; font-weight: bold`,
              `color: ${foregroundColor}; font-weight: normal`,
              ...consoleArgs.slice(0, formatSpecifierCount),
              ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
              ...consoleArgs.slice(formatSpecifierCount),
            );
            console.log(stackTrace.trim());
            console.groupEnd();
            // Update auto-expand timestamp if we just showed an expanded stack
            if (isAutoStack && !stackCollapsed) {
              stackAutoExpandMap.set(namespace, now);
            }
          } else {
            consoleMethod(
              `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
              ...(timeStr ? ["color: gray; font-weight: normal"] : []),
              `color: ${displayColor}; font-weight: bold`,
              `color: ${foregroundColor}; font-weight: normal`,
              ...consoleArgs.slice(0, formatSpecifierCount),
              ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
              ...consoleArgs.slice(formatSpecifierCount),
            );
          }
          throttleInfo.lastShownTime = now;
        }
      }
    } else {
      // Auto-throttle disabled, show normally
      const padding = alignEnabled
        ? " ".repeat(Math.max(0, longestNamespace - namespace.length))
        : "";

      // Call hooks for filtered console output (normal non-throttled)
      const filteredEntry: DebugLogEntry = {
        namespace,
        level: logLevel,
        message: formatMessage(
          actualFormat,
          processedArgs,
          config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
        ),
        template: actualFormat,
        formattedMessage: formatMessageToHtml(
          formatMessage(
            actualFormat,
            processedArgs,
            config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
          ),
          processedArgs,
          namespace,
          displayColor,
          config?.foreground,
          config?.objectDepth ?? DEFAULT_OBJECT_DEPTH,
          config,
          perfStr,
          0,
          timeStr,
          stackTrace,
          stackCollapsed,
        ),
        args: processedArgs,
        timestamp: now,
      };
      callFilteredHooks(filteredEntry);

      if (stackTrace) {
        const groupFn = stackCollapsed ? console.groupCollapsed : console.group;
        groupFn(
          `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
          ...(timeStr ? ["color: gray; font-weight: normal"] : []),
          `color: ${displayColor}; font-weight: bold`,
          `color: ${foregroundColor}; font-weight: normal`,
          ...consoleArgs.slice(0, formatSpecifierCount),
          ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
          ...consoleArgs.slice(formatSpecifierCount),
        );
        console.log(stackTrace.trim());
        console.groupEnd();
        // Update auto-expand timestamp if we just showed an expanded stack
        if (isAutoStack && !stackCollapsed) {
          stackAutoExpandMap.set(namespace, now);
        }
      } else {
        consoleMethod(
          `${timeStr}%c${namespace}%c${padding} ${consoleFormat}${perfStr ? `%c${perfStr}` : ""}`,
          ...(timeStr ? ["color: gray; font-weight: normal"] : []),
          `color: ${displayColor}; font-weight: bold`,
          `color: ${foregroundColor}; font-weight: normal`,
          ...consoleArgs.slice(0, formatSpecifierCount),
          ...(perfStr ? [`color: ${displayColor}; font-weight: bold`] : []),
          ...consoleArgs.slice(formatSpecifierCount),
        );
      }
    }
  };
};

// Add enable/disable methods
(DebugLib as any).enable = (pattern: string) => {
  localStorage.setItem("debug", pattern);
};

(DebugLib as any).disable = () => {
  localStorage.removeItem("debug");
};

// Enhanced debug wrapper with throttled method
export interface EnhancedDebug {
  (format: string, ...args: any[]): void;
  throttled: (format: string, ...args: any[]) => void;
}

export const createDebug = (
  namespace: string,
  configOrThrottleMs?: Partial<DebugConfig> | number,
): EnhancedDebug => {
  // Support legacy throttleMs parameter or new config object
  let defaultConfig: Partial<DebugConfig> | undefined;
  let throttleMs = 2000;

  if (typeof configOrThrottleMs === "number") {
    // Legacy support for throttleMs
    throttleMs = configOrThrottleMs;
  } else if (configOrThrottleMs) {
    // New config object
    defaultConfig = configOrThrottleMs;
    throttleMs = defaultConfig.throttleCooldown ?? 2000;
  }

  const normalDebug = DebugLib(namespace, defaultConfig);

  // Create throttled version that uses the same auto-throttle bucket
  const throttled = (format: string, ...args: any[]) => {
    // Get or initialize throttle info for this namespace (shared with auto-throttle)
    if (!autoThrottleMap.has(namespace)) {
      autoThrottleMap.set(namespace, {
        timestamps: [],
        suppressedCount: 0,
        lastShownTime: 0,
      });
    }

    const throttleInfo = autoThrottleMap.get(namespace)!;
    const now = Date.now();

    // Use config cooldown or fallback to throttleMs parameter
    const config = getNamespaceConfig(namespace);
    const cooldown = config?.throttleCooldown ?? throttleMs;

    // Store the latest message WITH THE FUCKING STACK
    const capturedStack = new Error().stack; // Always capture for throttled
    throttleInfo.lastMessage = { format, args, capturedStack };

    if (now - throttleInfo.lastShownTime >= cooldown) {
      // Show immediately (leading edge)
      normalDebug(format, ...args);
      throttleInfo.lastShownTime = now;
      throttleInfo.suppressedCount = 0;

      // Clear any pending trailing call
      if (throttleInfo.trailingTimeout) {
        clearTimeout(throttleInfo.trailingTimeout as any);
        throttleInfo.trailingTimeout = undefined;
      }
    } else {
      // Suppressed - increment count
      throttleInfo.suppressedCount++;

      // Clear existing timeout and set new one for trailing edge
      if (throttleInfo.trailingTimeout) {
        clearTimeout(throttleInfo.trailingTimeout as any);
      }

      // Set timeout to show the last message after cooldown
      throttleInfo.trailingTimeout = setTimeout(
        () => {
          if (throttleInfo.lastMessage && throttleInfo.suppressedCount > 0) {
            const {
              format: lastFormat,
              args: lastArgs,
              capturedStack,
            } = throttleInfo.lastMessage;
            // normalDebug will show the count via throttleInfo.suppressedCount
            // Pass the captured stack if we have it
            if (capturedStack) {
              normalDebug(
                { __capturedStack: capturedStack },
                lastFormat,
                ...lastArgs,
              );
            } else {
              normalDebug(lastFormat, ...lastArgs);
            }
            // Reset AFTER calling normalDebug so it can see the count
            throttleInfo.lastShownTime = Date.now();
            throttleInfo.suppressedCount = 0;
            throttleInfo.trailingTimeout = undefined;
            throttleInfo.lastMessage = undefined;
          }
        },
        cooldown - (now - throttleInfo.lastShownTime),
      );
    }
  };

  // Default colors for common log levels (namespace colors)
  const levelColors: Record<string, string> = {
    error: "#ff0000",
    warn: "#ffa500",
    warning: "#ffa500",
    info: "#0080ff",
    log: "inherit",
    debug: "#808080",
    verbose: "#808080",
    success: "#00ff00",
  };

  // Default foreground colors for common log levels (message text colors)
  const levelForegroundColors: Record<string, string> = {
    error: "#ff0000", // Red for error messages
    warn: "#ffa500", // Orange for warnings
    warning: "#ffa500", // Orange for warnings
    info: "#00ccff", // Cyan for info
    debug: "#808080", // Gray for debug
    verbose: "#808080", // Gray for verbose
    success: "#00ff00", // Green for success
    // log: inherit (no default foreground)
  };

  // Create level-specific debug functions
  const createLevelDebug = (level: string) => {
    return (format: string | any, ...args: any[]) => {
      // Create a new debug instance with the level appended
      const levelNamespace = `${namespace}:${level}`;

      // Get configs with parent inheritance
      // This will get configs for "a", then "a:b", then "a:b:error" and merge them
      const inheritedConfig = getNamespaceConfig(levelNamespace, true);

      // Also get the parent config separately for color defaults check
      const parentConfig = getNamespaceConfig(namespace);

      // Merge configs with level-specific defaults
      const mergedConfig = {
        ...defaultConfig,
        ...inheritedConfig,
        // Add default namespace color if not already specified in any config
        ...(levelColors[level.toLowerCase()] &&
        !inheritedConfig?.color &&
        !parentConfig?.color
          ? { color: levelColors[level.toLowerCase()] }
          : {}),
        // Add default foreground color if not already specified in any config
        ...(levelForegroundColors[level.toLowerCase()] &&
        !inheritedConfig?.foreground &&
        !parentConfig?.foreground
          ? { foreground: levelForegroundColors[level.toLowerCase()] }
          : {}),
      };

      const levelDebug = DebugLib(levelNamespace, mergedConfig);
      levelDebug(format, ...args);
    };
  };

  // Create dynamic level method
  const level = (levelName: string, format: string | any, ...args: any[]) => {
    createLevelDebug(levelName)(format, ...args);
  };

  // Create enhanced debug function with throttled property and level methods
  const enhanced = Object.assign(normalDebug, {
    throttled,
    error: createLevelDebug("error"),
    warn: createLevelDebug("warn"),
    info: createLevelDebug("info"),
    log: createLevelDebug("log"),
    debug: createLevelDebug("debug"),
    level,
  }) as EnhancedDebug;

  return enhanced;
};

// Set up debug controls immediately when module loads
if (typeof window !== "undefined") {
  (window as any).debug = {
    // List available functions
    help: () => {
      console.log("Debug functions:");
      console.log("  debug.enable(pattern)  - Enable debug patterns");
      console.log("  debug.disable()        - Disable all debug output");
      console.log("  debug.restore()        - Restore backed-up pattern");
      console.log("  debug.config(str)      - Set debug configuration");
      console.log("  debug.clearConfig()    - Clear debug configuration");
      console.log(
        "  debug.reloadConfig()   - Reload configs from localStorage",
      );
      console.log("  debug.test()           - Test debug output");
      console.log("  debug.help()           - Show this help");
    },
    enable: (pattern: string = "*") => {
      // Backup existing pattern if it's not '*'
      const existing = localStorage.getItem("debug");
      if (existing && existing !== "*") {
        localStorage.setItem("debug_backup", existing);
      }

      localStorage.setItem("debug", pattern);
      (DebugLib as any).enable(pattern);
      console.log("Debug enabled for:", pattern);
      console.log("Pattern formats:");
      console.log("  *                           - enable all logs");
      console.log("  bikedash:*                  - enable all bikedash logs");
      console.log(
        "  -bikedash:map:*             - disable map logs (minus prefix)",
      );
      console.log("  bikedash:*,-bikedash:map:*  - enable all except map");
      console.log("  (patterns can be separated by commas or newlines)");
      console.log('  (use "# " for comments to end of line)');
      console.log("Inline config:");
      console.log("  bikedash:*{threshold:5}     - enable with config");
      console.log(
        "  ~bikedash:*{autoThrottle:true} - config only (tilde prefix)",
      );
      console.log(
        "  bikedash:app:*{collapse:true,threshold:10} - multiple options",
      );
      console.log("Examples:");
      console.log(
        "  bikedash:*{threshold:5},-bikedash:map:*,~bikedash:gps:*{autoThrottle:true}",
      );
      console.log("Multiline example with comments:");
      console.log("  bikedash:*              # enable all bikedash logs");
      console.log("  -bikedash:map:*         # but disable map logs");
      console.log("  ~bikedash:gps:*{autoThrottle:true}  # config for GPS");

      // Test it immediately
      const test = createDebug("bikedash:test");
      test("Debug is now enabled and working with colors!");
    },
    disable: () => {
      // Backup existing pattern if it's not empty and not '*'
      const existing = localStorage.getItem("debug");
      if (existing && existing !== "*") {
        localStorage.setItem("debug_backup", existing);
      }

      localStorage.removeItem("debug");
      (DebugLib as any).disable();
      console.log("Debug disabled.");
    },
    restore: () => {
      const backup = localStorage.getItem("debug_backup");
      if (backup) {
        localStorage.setItem("debug", backup);
        (DebugLib as any).enable(backup);
        console.log("Debug restored to:", backup);
      } else {
        console.log("No backup pattern found.");
      }
    },
    config: (configStr: string) => {
      localStorage.setItem("debugConfig", configStr);
      console.log("Debug config set:", configStr);
      console.log("Built-in configs (use with # in patterns):");
      console.log(
        "  #throttle.default    - Standard auto-throttle (10 msgs/5s, 0.9s cooldown)",
      );
      console.log(
        "  #throttle.strict     - For :throttled namespaces (5 msgs/3s, 2s cooldown)",
      );
      console.log(
        "  #throttle.aggressive - Heavy throttling (3 msgs/2s, 3s cooldown)",
      );
      console.log(
        "  #throttle.mild       - Light throttling (20 msgs/10s, 0.5s cooldown)",
      );
      console.log(
        "  #throttle.realtime   - For high-frequency updates (10 msgs/1s, 0.1s cooldown)",
      );
      console.log("  #throttle.off        - Disable auto-throttle");
      console.log(
        "  #objects.collapse    - Show object summaries instead of full expansion",
      );
      console.log(
        "  #objects.expand      - Show full object expansion (default)",
      );
      console.log(
        "  #objects.smart       - Auto-collapse objects when throttling is active",
      );
    },
    clearConfig: () => {
      localStorage.removeItem("debugConfig");
      console.log("Debug config cleared.");
    },
    reload: () => {
      // Clear cached configs to force reload from localStorage
      namedConfigs.clear();

      // Force re-read from localStorage by triggering parseDebugConfig
      // This will happen automatically on the next debug call
      const debugSetting = localStorage.getItem("debug");
      const debugConfig = localStorage.getItem("debugConfig");

      if (debugSetting) {
        (DebugLib as any).enable(debugSetting);
      }

      console.log("Configurations reloaded from localStorage");
      if (debugSetting) {
        console.log(
          "  debug:",
          debugSetting.split(/[,\n]/).slice(0, 3).join(", ") +
            (debugSetting.split(/[,\n]/).length > 3 ? "..." : ""),
        );
      }
      if (debugConfig) {
        console.log(
          "  debugConfig:",
          debugConfig.split(/[,\n]/).slice(0, 3).join(", ") +
            (debugConfig.split(/[,\n]/).length > 3 ? "..." : ""),
        );
      }

      // Test that the config works
      const test = createDebug("bikedash:config-test");
      test("Config reloaded successfully");
    },
    test: () => {
      const test = createDebug("bikedash:test");
      test("Test message - if you see this with colors, debug is working!");
      test.throttled("Throttled test message");

      // Test auto-throttle
      console.log("Testing auto-throttle - sending 20 rapid messages...");
      for (let i = 0; i < 20; i++) {
        test(`Rapid message ${i + 1}`);
      }
    },
  };
}

// Keep the function for compatibility but it's now a no-op
export const setupDebugControls = () => {
  // Controls are already set up when module loads
};

// Add debugHooks to createDebug as a property
(createDebug as any).debugHooks = debugHooks;

// Default export for drop-in compatibility with debug library
// Usage: import debug from 'debugx';
//        const log = debug('namespace');
//        debug.debugHooks.addFiltered(...)
// Export both named and default for different import styles
export const debugx = createDebug;
export default createDebug;
